import React, {
	useEffect,
	useState,
	useRef,
	useCallback,
	useMemo,
} from "react";
import {
	ChevronLeft,
	ChevronRight,
	MessageSquare,
	Send,
	Edit2,
	Save,
	X,
	Download,
	Info,
	Trash2,
	Flag,
	Calendar,
} from "lucide-react";
import {
	CircularProgress,
	Box,
	Typography,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	TextField,
	Button,
	List,
	ListItem,
	ListItemAvatar,
	ListItemText,
	Avatar,
	Divider,
	IconButton,
} from "@mui/material";
import { format } from "date-fns";
import { enGB } from "date-fns/locale";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import Tooltip from "@mui/material/Tooltip";
import { getApiBaseUrl } from "../../utils/apiConfig";
import { apiClient } from "../../services/api";
import { useUserAccess } from "../../hooks/useUserAccess";
import PTSachNrSidePanel from "../Sidebars/PTSachNrSidePanel";

// Define API base URL (keeping for backward compatibility, but using getApiBaseUrl() for API calls)
const API_BASE_URL =
	process.env.REACT_APP_API_BASE_URL || "http://localhost:8000/api";

const CommentModal = ({
	open,
	onClose,
	snr,
	itemType,
	onAddComment,
	newComment,
	setNewComment,
	editingCommentId,
	setEditingCommentId,
	editText,
	setEditText,
	getUserDisplayName,
	canWrite,
}) => {
	const [comments, setComments] = useState([]);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [deletingCommentId, setDeletingCommentId] = useState(null);
	const [confirmDeleteId, setConfirmDeleteId] = useState(null);
	// Get current user name — prefer the display name from the OIDC profile, but fall
	// back to the username stored in localStorage (set from the JWT at login) so we
	// never accidentally save "Current User" to the database.
	const currentUser =
		(getUserDisplayName ? getUserDisplayName() : null) ||
		localStorage.getItem("username") ||
		"Current User";

	// Drag state
	const [position, setPosition] = useState({ x: null, y: null });
	const dragRef = useRef({
		dragging: false,
		startX: 0,
		startY: 0,
		originX: 0,
		originY: 0,
	});

	const handleDragStart = React.useCallback(
		(e) => {
			if (e.target.closest("button")) return;
			const paperEl =
				e.currentTarget
					.closest('[role="dialog"]')
					?.querySelector(".MuiDialog-paper") || e.currentTarget.parentElement;
			const rect = paperEl?.getBoundingClientRect() || { left: 0, top: 0 };
			dragRef.current = {
				dragging: true,
				startX: e.clientX,
				startY: e.clientY,
				originX: position.x !== null ? position.x : rect.left,
				originY: position.y !== null ? position.y : rect.top,
			};
			e.preventDefault();
		},
		[position],
	);

	useEffect(() => {
		const onMouseMove = (e) => {
			if (!dragRef.current.dragging) return;
			const dx = e.clientX - dragRef.current.startX;
			const dy = e.clientY - dragRef.current.startY;
			setPosition({
				x: dragRef.current.originX + dx,
				y: dragRef.current.originY + dy,
			});
		};
		const onMouseUp = () => {
			dragRef.current.dragging = false;
		};
		document.addEventListener("mousemove", onMouseMove);
		document.addEventListener("mouseup", onMouseUp);
		return () => {
			document.removeEventListener("mousemove", onMouseMove);
			document.removeEventListener("mouseup", onMouseUp);
		};
	}, []);
	useEffect(() => {
		if (open && snr) {
			fetchComments();
		} // Reset editing states when modal closes
		if (!open) {
			setEditingCommentId(null);
			setEditText("");
			setPosition({ x: null, y: null });
		}
	}, [open, snr, itemType]);
	const fetchComments = async () => {
		try {
			setLoading(true);
			const categoryParam = itemType ? `&category=${itemType}` : "";
			const response = await apiClient.get(
				`/api/comments?snr=${snr}${categoryParam}`,
			);
			setComments(response.data.comments || []);
		} catch (error) {
			console.error("Error fetching comments:", error);
		} finally {
			setLoading(false);
		}
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		if (!newComment.trim()) return;
		setSaving(true);
		try {
			const response = await apiClient.post("/api/comments", {
				snr,
				text: newComment,
				user: currentUser,
				category: itemType, // Send the item type as category
			});

			if (response.data) {
				setNewComment("");
				await fetchComments();
				if (onAddComment) onAddComment();
			}
		} catch (error) {
			console.error("Error adding comment:", error);
		} finally {
			setSaving(false);
		}
	};
	const handleUpdateComment = async (commentId) => {
		if (!editText.trim()) return;
		setSaving(true);
		try {
			const response = await apiClient.put(`/api/comments/${commentId}`, {
				snr,
				text: editText,
				user: currentUser,
				category: itemType, // Send the item type as category
			});

			if (response.data) {
				setEditingCommentId(null);
				setEditText("");
				await fetchComments();
			}
		} catch (error) {
			console.error("Error updating comment:", error);
		} finally {
			setSaving(false);
		}
	};
	const handleDeleteComment = (commentId) => {
		// Show inline confirmation instead of window.confirm (which blocks rendering)
		setConfirmDeleteId(commentId);
	};

	const handleConfirmDelete = async (commentId) => {
		setConfirmDeleteId(null);
		setDeletingCommentId(commentId);
		setSaving(true);
		try {
			const response = await apiClient.delete(`/api/comments/${commentId}`, {
				data: { snr, user: currentUser },
			});
			if (response.data && response.data.success) {
				await fetchComments();
			} else {
				alert("Error deleting comment. Please try again.");
			}
		} catch (error) {
			console.error("Error deleting comment:", error);
			if (error?.response?.status === 403) {
				alert("Access denied. You do not have permission to delete comments.");
			} else {
				alert(`Failed to delete comment: ${error.message || "Unknown error"}`);
			}
		} finally {
			setSaving(false);
			setDeletingCommentId(null);
		}
	};

	const formatDate = (dateString) => {
		try {
			if (!dateString) return "";
			const date = new Date(dateString);
			return format(date, "MMM d, yyyy h:mm a");
		} catch (e) {
			return "";
		}
	};
	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth="md"
			fullWidth
			PaperProps={{
				style: {
					borderRadius: "0px",
					boxShadow: "0 10px 40px rgba(30, 40, 50, 0.15)",
					overflow: "hidden",
					height: "70vh",
					maxHeight: "700px",
					...(position.x !== null
						? {
								position: "fixed",
								left: position.x,
								top: position.y,
								margin: 0,
								transform: "none",
							}
						: {}),
				},
			}}
		>
			{/* Header */}
			<Box
				onMouseDown={handleDragStart}
				sx={{
					backgroundColor: "#1e2832",
					color: "white",
					px: 3,
					py: 2,
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
					cursor: "grab",
					userSelect: "none",
					"&:active": { cursor: "grabbing" },
				}}
			>
				<Box display="flex" alignItems="center" gap={2}>
					<MessageSquare size={20} />{" "}
					<Typography
						variant="h6"
						sx={{
							fontWeight: 600,
							fontSize: "16px",
							fontFamily: "MAN Europe_Bold",
							letterSpacing: "0.5px",
						}}
					>
						Comments{" "}
						{itemType &&
							`- ${itemType.charAt(0).toUpperCase() + itemType.slice(1)}`}
					</Typography>
					<Typography
						variant="body2"
						sx={{
							color: "rgba(255, 255, 255, 0.7)",
							fontSize: "14px",
						}}
					>
						SNR: {snr}
					</Typography>
				</Box>
				<IconButton
					onClick={onClose}
					sx={{
						color: "white",
						"&:hover": {
							backgroundColor: "rgba(255, 255, 255, 0.1)",
							transform: "scale(1.05)",
						},
						transition: "all 0.2s ease",
					}}
				>
					<X size={18} />
				</IconButton>
			</Box>

			{/* Content */}
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					height: "calc(100% - 70px)",
				}}
			>
				{/* Comments List */}
				<Box
					sx={{
						flex: 1,
						overflowY: "auto",
						backgroundColor: "#f8f9fa",
						"&::-webkit-scrollbar": {
							width: "6px",
						},
						"&::-webkit-scrollbar-track": {
							background: "#f1f1f1",
						},
						"&::-webkit-scrollbar-thumb": {
							background: "#c1c1c1",
							borderRadius: "3px",
						},
						"&::-webkit-scrollbar-thumb:hover": {
							background: "#a8a8a8",
						},
					}}
				>
					{loading ? (
						<Box
							display="flex"
							justifyContent="center"
							alignItems="center"
							height="100%"
							py={4}
						>
							<CircularProgress sx={{ color: "#cc0000" }} size={32} />
							<Typography variant="body2" sx={{ ml: 2, color: "#666" }}>
								Loading comments...
							</Typography>
						</Box>
					) : comments.length > 0 ? (
						<Box sx={{ p: 2 }}>
							{comments.map((item, index) => (
								<Box
									key={item.id || `comment-${index}`}
									sx={{
										backgroundColor: "white",
										borderRadius: "0px",
										p: 2.5,
										mb: 1.5,
										border: "1px solid #e1e5e9",
										boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
										transition: "all 0.2s ease",
										"&:hover": {
											boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
											transform: "translateY(-1px)",
										},
										"&:last-child": {
											mb: 0,
										},
									}}
								>
									{editingCommentId === item.id ? (
										<Box>
											<TextField
												fullWidth
												multiline
												rows={2}
												value={editText}
												onChange={(e) => setEditText(e.target.value)}
												variant="outlined"
												size="small"
												autoFocus
												placeholder="Edit your comment..."
												sx={{
													"& .MuiOutlinedInput-root": {
														borderRadius: "0px",
														fontSize: "14px",
														"&:hover fieldset": { borderColor: "#1e2832" },
														"&.Mui-focused fieldset": {
															borderColor: "#1e2832",
														},
													},
												}}
											/>
											<Box
												display="flex"
												justifyContent="flex-end"
												gap={1}
												mt={1.5}
											>
												<Button
													size="small"
													onClick={() => {
														setEditingCommentId(null);
														setEditText("");
													}}
													disabled={saving}
													sx={{
														color: "#666",
														textTransform: "none",
														fontSize: "12px",
														minWidth: "60px",
													}}
												>
													Cancel
												</Button>
												<Button
													variant="contained"
													size="small"
													onClick={() => handleUpdateComment(item.id)}
													disabled={saving || !editText.trim()}
													sx={{
														backgroundColor: "#cc0000",
														textTransform: "none",
														fontSize: "12px",
														minWidth: "60px",
														"&:hover": { backgroundColor: "#b30000" },
													}}
												>
													{saving ? "Saving..." : "Save"}
												</Button>
											</Box>
										</Box>
									) : (
										<Box>
											<Box
												display="flex"
												justifyContent="space-between"
												alignItems="center"
												mb={1}
											>
												<Box display="flex" alignItems="center" gap={1}>
													<Box
														sx={{
															width: "32px",
															height: "32px",
															borderRadius: "50%",
															backgroundColor: "#cc0000",
															display: "flex",
															alignItems: "center",
															justifyContent: "center",
															color: "white",
															fontSize: "12px",
															fontWeight: "bold",
														}}
													>
														{" "}
														{(item.user || "S").charAt(0).toUpperCase()}
													</Box>
													<Typography
														variant="subtitle2"
														sx={{
															fontWeight: 600,
															color: "#1e2832",
															fontSize: "13px",
														}}
													>
														{item.user || "System"}
													</Typography>
												</Box>
												<Box display="flex" alignItems="center" gap={1}>
													<Typography
														variant="caption"
														sx={{
															color: "#999",
															fontSize: "11px",
															fontStyle: "MAN Europe_Bold",
														}}
													>
														{formatDate(item.timestamp)}
													</Typography>{" "}
													{!item.is_legacy && canWrite && (
														<Box display="flex" alignItems="center" gap={0.5}>
															<IconButton
																size="small"
																onClick={() => {
																	setEditingCommentId(item.id);
																	setEditText(item.text);
																}}
																disabled={saving}
																sx={{
																	color: "#999",
																	padding: "4px",
																	"&:hover": {
																		backgroundColor: "rgba(204, 0, 0, 0.08)",
																		color: "#cc0000",
																	},
																}}
																title="Edit comment"
															>
																<Edit2 size={14} />{" "}
															</IconButton>

															{confirmDeleteId === item.id ? (
																<Box
																	display="flex"
																	alignItems="center"
																	gap={0.5}
																>
																	<Typography
																		variant="caption"
																		sx={{
																			fontSize: "11px",
																			color: "#dc2626",
																			fontWeight: 600,
																		}}
																	>
																		Delete?
																	</Typography>
																	<IconButton
																		size="small"
																		onClick={() => handleConfirmDelete(item.id)}
																		sx={{
																			color: "#dc2626",
																			padding: "3px",
																			"&:hover": {
																				backgroundColor:
																					"rgba(220, 38, 38, 0.1)",
																			},
																		}}
																		title="Confirm delete"
																	>
																		<span
																			style={{
																				fontSize: "11px",
																				fontWeight: 700,
																			}}
																		>
																			✓
																		</span>
																	</IconButton>
																	<IconButton
																		size="small"
																		onClick={() => setConfirmDeleteId(null)}
																		sx={{
																			color: "#6b7280",
																			padding: "3px",
																			"&:hover": {
																				backgroundColor: "rgba(0,0,0,0.05)",
																			},
																		}}
																		title="Cancel"
																	>
																		<span
																			style={{
																				fontSize: "11px",
																				fontWeight: 700,
																			}}
																		>
																			✕
																		</span>
																	</IconButton>
																</Box>
															) : (
																<IconButton
																	size="small"
																	onClick={() => handleDeleteComment(item.id)}
																	disabled={saving}
																	sx={{
																		color: "#999",
																		padding: "4px",
																		"&:hover": {
																			backgroundColor:
																				"rgba(220, 38, 38, 0.08)",
																			color: "#dc2626",
																		},
																	}}
																	title="Delete comment"
																>
																	{deletingCommentId === item.id ? (
																		<CircularProgress
																			size={14}
																			sx={{ color: "#dc2626" }}
																		/>
																	) : (
																		<Trash2 size={14} />
																	)}
																</IconButton>
															)}
														</Box>
													)}
												</Box>
											</Box>
											<Typography
												variant="body2"
												sx={{
													whiteSpace: "pre-wrap",
													color: "#333",
													lineHeight: 1.6,
													fontSize: "13px",
													ml: 5, // Indent text to align with avatar
												}}
											>
												{item.text}
											</Typography>
										</Box>
									)}
								</Box>
							))}
						</Box>
					) : (
						<Box
							display="flex"
							flexDirection="column"
							alignItems="center"
							justifyContent="center"
							height="100%"
							sx={{ color: "#666", py: 4 }}
						>
							<MessageSquare size={40} color="#d1d5db" />
							<Typography
								variant="body1"
								sx={{
									mt: 2,
									fontWeight: 500,
									color: "#4b5563",
								}}
							>
								No comments yet
							</Typography>
							<Typography
								variant="body2"
								sx={{
									color: "#9ca3af",
									fontSize: "13px",
									textAlign: "center",
									mt: 0.5,
								}}
							>
								Start the conversation by adding the first comment
							</Typography>
						</Box>
					)}
				</Box>{" "}
				{/* Add Comment Form */}
				{canWrite ? (
					<Box
						component="form"
						onSubmit={handleSubmit}
						sx={{
							p: 2.5,
							borderTop: "1px solid #e1e5e9",
							backgroundColor: "white",
							boxShadow: "0 -2px 8px rgba(0, 0, 0, 0.05)",
						}}
					>
						<TextField
							fullWidth
							variant="outlined"
							placeholder="Write a comment..."
							value={newComment}
							onChange={(e) => setNewComment(e.target.value)}
							multiline
							rows={2}
							disabled={saving}
							sx={{
								"& .MuiOutlinedInput-root": {
									borderRadius: "0px",
									fontSize: "14px",
									backgroundColor: "#fafafa",
									"&:hover": {
										backgroundColor: "#f5f5f5",
									},
									"&:hover fieldset": { borderColor: "#1e2832" },
									"&.Mui-focused": {
										backgroundColor: "white",
									},
									"&.Mui-focused fieldset": { borderColor: "#1e2832" },
								},
							}}
						/>
						<Box
							sx={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								mt: 2,
							}}
						>
							<Typography
								variant="caption"
								sx={{
									color: "#9ca3af",
									fontSize: "11px",
									fontStyle: "MAN Europe_Bold",
								}}
							>
								Press Enter + Shift for a new line
							</Typography>
							<Box sx={{ display: "flex", gap: 1.5 }}>
								<Button
									variant="outlined"
									onClick={onClose}
									disabled={saving}
									sx={{
										textTransform: "none",
										color: "#6b7280",
										borderRadius: "0px",
										fontFamily: "MAN Europe_Bold",
										borderColor: "#d1d5db",
										fontSize: "13px",
										minWidth: "70px",
										"&:hover": {
											borderColor: "#1e2832",
											backgroundColor: "rgba(30, 40, 50, 0.04)",
										},
									}}
								>
									Close
								</Button>
								<Button
									type="submit"
									variant="contained"
									disabled={!newComment.trim() || saving}
									sx={{
										textTransform: "none",
										backgroundColor: "#cc0000",
										fontSize: "13px",
										fontFamily: "MAN Europe_Bold",
										minWidth: "80px",
										borderRadius: "0px",
										fontWeight: 600,
										"&:hover": { backgroundColor: "#b30000" },
										"&.Mui-disabled": {
											backgroundColor: "#e5e7eb",
											color: "#9ca3af",
										},
									}}
								>
									{saving ? "Adding..." : "Add Comment"}
								</Button>
							</Box>
						</Box>
					</Box>
				) : (
					<Box
						sx={{
							p: 2.5,
							borderTop: "1px solid #e1e5e9",
							backgroundColor: "#f9fafb",
							textAlign: "center",
						}}
					>
						<Typography
							variant="body2"
							sx={{
								color: "#6b7280",
								fontSize: "13px",
								fontStyle: "italic",
							}}
						>
							🔒 You have read-only access. Write permission is required to add
							comments.
						</Typography>
						<Button
							variant="outlined"
							onClick={onClose}
							sx={{
								mt: 2,
								textTransform: "none",
								color: "#6b7280",
								borderColor: "#d1d5db",
								fontSize: "13px",
								minWidth: "70px",
								"&:hover": {
									borderColor: "#1e2832",
									backgroundColor: "rgba(30, 40, 50, 0.04)",
								},
							}}
						>
							Close
						</Button>
					</Box>
				)}
			</Box>
		</Dialog>
	);
};

// Helper to format a Date to YYYY-MM-DD without timezone shifting
const formatDateForBackend = (date) => {
	if (!date) return null;
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");
	return `${year}-${month}-${day}`;
};

// Function to convert data to CSV format
const convertToCSV = (data) => {
	if (!data || data.length === 0) return "";

	// Get all unique keys from all items
	const headers = [];
	data.forEach((item) => {
		Object.keys(item).forEach((key) => {
			if (!headers.includes(key)) {
				headers.push(key);
			}
		});
	});

	// Create header row
	let csvContent = headers.join(",") + "\n";

	// Add data rows
	data.forEach((item) => {
		const row = headers.map((header) => {
			// Escape quotes and wrap in quotes if the value contains commas, quotes, or newlines
			const value = item[header] !== undefined ? item[header] : "";
			const escapedValue = String(value).replace(/"/g, '""');
			return `"${escapedValue}"`;
		});
		csvContent += row.join(",") + "\n";
	});

	return csvContent;
};

// Function to trigger CSV download
const downloadCSV = (data, filename = "export.csv") => {
	const csvContent = convertToCSV(data);
	const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
	const url = URL.createObjectURL(blob);
	const link = document.createElement("a");
	link.setAttribute("href", url);
	link.setAttribute("download", filename);
	link.style.visibility = "hidden";
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
};

// SE Team edit cell — select dropdown styled like BY SET, with free-text fallback + tooltip
const SeTeamEditCell = ({ value, options, onChange }) => {
	const isManual = value !== "" && !options.includes(value);
	const [showManual, setShowManual] = React.useState(isManual);
	const [showTooltip, setShowTooltip] = React.useState(false);

	const selectStyle = {
		width: "100%",
		padding: "3px",
		border: "1px solid #ccc",
		borderRadius: "0px",
		fontSize: "11px",
		backgroundColor: "white",
	};

	const handleSelectChange = (e) => {
		if (e.target.value === "__manual__") {
			setShowManual(true);
			onChange("");
		} else {
			setShowManual(false);
			setShowTooltip(false);
			onChange(e.target.value);
		}
	};

	const handleTextChange = (e) => {
		onChange(e.target.value);
		setShowTooltip(e.target.value.length > 0);
	};

	return (
		<div style={{ position: "relative" }}>
			{/* Always show the select */}
			<select
				value={showManual ? "__manual__" : value || ""}
				onChange={handleSelectChange}
				style={selectStyle}
			>
				<option value="">Select</option>
				<option value="__manual__">Other (type manually…)</option>
				{options.map((team) => (
					<option key={team} value={team}>
						{team}
					</option>
				))}
			</select>

			{/* Free-text input shown when manual entry is chosen */}
			{showManual && (
				<input
					type="text"
					value={value}
					onChange={handleTextChange}
					onFocus={() => setShowTooltip(value.length > 0)}
					onBlur={() => setShowTooltip(false)}
					placeholder="Type SE-Team…"
					autoFocus
					style={{
						...selectStyle,
						marginTop: "4px",
						border: "1px solid #cc0000",
					}}
				/>
			)}

			{/* Tooltip shown as soon as the user starts typing manually */}
			{showManual && showTooltip && (
				<div
					style={{
						position: "absolute",
						top: "100%",
						left: 0,
						marginTop: "4px",
						backgroundColor: "#1e2832",
						color: "white",
						fontSize: "11px",
						padding: "6px 8px",
						borderRadius: "0px",
						width: "220px",
						zIndex: 9999,
						lineHeight: "1.4",
						boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
						pointerEvents: "none",
					}}
				>
					<span style={{ fontFamily: "MAN EUROPE_Bold" }}>ℹ️ </span>
					As you are manually adding an SE-Team, please refresh the page to see
					the value in the dropdown for other records.
					{/* Arrow */}
					<div
						style={{
							position: "absolute",
							top: "-5px",
							left: "12px",
							width: 0,
							height: 0,
							borderLeft: "5px solid transparent",
							borderRight: "5px solid transparent",
							borderBottom: "5px solid #1e2832",
						}}
					/>
				</div>
			)}
		</div>
	);
};

// Access Denied Modal — shown when the backend rejects a write with HTTP 403
const AccessDeniedModal = ({ open, onClose }) => {
	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth="sm"
			fullWidth
			PaperProps={{ style: { borderRadius: "5px", padding: "8px" } }}
		>
			<DialogContent
				style={{ textAlign: "center", padding: "32px 24px 24px 24px" }}
			>
				<div
					style={{
						width: "64px",
						height: "64px",
						backgroundColor: "#1e2832",
						borderRadius: "50%",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						margin: "0 auto 24px auto",
						border: "3px solid #1e2832",
					}}
				>
					<div style={{ fontSize: "32px", color: "#cc0000" }}>🔒</div>
				</div>

				<Typography
					variant="h6"
					style={{
						marginBottom: "16px",
						fontFamily: "MAN EUROPE_Bold",
						color: "#333",
						fontSize: "20px",
					}}
				>
					Access Denied
				</Typography>

				<Typography
					variant="body1"
					style={{
						color: "#666",
						lineHeight: "1.6",
						fontSize: "16px",
						marginBottom: "8px",
					}}
				>
					You have <strong>read-only</strong> access and cannot make changes.
				</Typography>

				<Typography variant="body2" style={{ color: "#888", fontSize: "14px" }}>
					Please contact your administrator if you need write access.
				</Typography>
			</DialogContent>

			<DialogActions
				style={{ padding: "0 24px 24px 24px", justifyContent: "center" }}
			>
				<Button
					onClick={onClose}
					variant="contained"
					style={{
						backgroundColor: "#cc0000",
						color: "white",
						borderRadius: "5px",
						padding: "10px 32px",
						fontFamily: "MAN EUROPE_Bold",
						textTransform: "none",
						fontSize: "14px",
					}}
					onMouseOver={(e) => (e.target.style.backgroundColor = "#b30000")}
					onMouseOut={(e) => (e.target.style.backgroundColor = "#cc0000")}
				>
					OK
				</Button>
			</DialogActions>
		</Dialog>
	);
};

// Warning Modal for rapid flag updates
const WarningModal = ({ open, onClose }) => {
	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth="sm"
			fullWidth
			PaperProps={{
				style: {
					borderRadius: "5px",
					padding: "8px",
				},
			}}
		>
			<DialogContent
				style={{ textAlign: "center", padding: "32px 24px 24px 24px" }}
			>
				<div
					style={{
						width: "64px",
						height: "64px",
						backgroundColor: "#1e2832",
						borderRadius: "50%",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						margin: "0 auto 24px auto",
						border: "3px solid #1e2832",
					}}
				>
					<div
						style={{
							fontSize: "32px",
							color: "#f39c12",
						}}
					>
						⏳
					</div>
				</div>

				<Typography
					variant="h6"
					style={{
						marginBottom: "16px",
						fontFamily: "MAN EUROPE_Bold",
						color: "#333",
						fontSize: "20px",
					}}
				>
					Please Wait a Moment!
				</Typography>

				<Typography
					variant="body1"
					style={{
						color: "#666",
						lineHeight: "1.6",
						fontSize: "16px",
						marginBottom: "8px",
						fontStyle: "MAN Europe_Bold",
					}}
				>
					We're still processing your previous update.
				</Typography>

				<Typography
					variant="body2"
					style={{
						color: "#888",
						fontSize: "14px",
						fontStyle: "MAN Europe_Bold",
					}}
				>
					Please wait for the current update to complete before making another
					change.
				</Typography>
			</DialogContent>

			<DialogActions
				style={{ padding: "0 24px 24px 24px", justifyContent: "center" }}
			>
				<Button
					onClick={onClose}
					variant="contained"
					style={{
						backgroundColor: "#cc0000",
						color: "white",
						borderRadius: "5px",
						padding: "10px 32px",
						fontFamily: "MAN EUROPE_Bold",
						textTransform: "none",
						fontSize: "14px",
					}}
					onMouseOver={(e) => (e.target.style.backgroundColor = "#b30000")}
					onMouseOut={(e) => (e.target.style.backgroundColor = "#cc0000")}
				>
					Got it!
				</Button>
			</DialogActions>
		</Dialog>
	);
};

const PTSachNrDataTable = React.forwardRef(
	(
		{
			onSelectItems,
			selectedItems,
			editOptions = {},
			columnVisibility = {
				rampUpGroup: true,
				totalReq: true,
				seTeams: true,
				purchaseType: true,
				supplier: true,
			},
			selectedViewType,
			getUserDisplayName, // Add getUserDisplayName prop
			getUserEmail, // Add getUserEmail prop for access control
			preventInitialLoad = false, // Add prop to prevent initial data loading when coming from dashboard
			isDashboardLoading = false, // Add prop to override loading state when dashboard filters are being applied
			showMismatchOnly = false, // Show only rows where BY SET and BY SYSTEM values mismatch
		},
		ref,
	) => {
		// Helper function to check if purchase types match and get styling for BY SET (no styling)
		const getPurchaseTypeStyleBySet = (item) => {
			return {
				backgroundColor: "transparent",
				color: "inherit",
				padding: "2px 4px",
				borderRadius: "3px",
				fontWeight: "normal",
				fontSize: "12px",
			};
		};
		// Helper function to check if purchase types match and get styling for BY SYSTEM
		// - Red background when BY SET and BY SYSTEM are both present but differ
		// - Green background when BY SET and BY SYSTEM are both present and equal
		// - Transparent (no highlight) when either value is missing
		const getPurchaseTypeStyleBySystem = (item) => {
			const bySet = (item.purchase_type || "").trim();
			const bySystem = (item.purchase_type_system || "").trim();
			const bothPresent =
				bySet && bySystem && bySet !== "-" && bySystem !== "-";

			if (bothPresent && bySet !== bySystem) {
				return {
					backgroundColor: "#e40045",
					color: "white",
					padding: "2px 4px",
					borderRadius: "3px",
					border: "1px solid #cc0000",
					fontSize: "12px",
				};
			}

			if (bothPresent && bySet === bySystem) {
				return {
					backgroundColor: "#16a34a",
					color: "white",
					padding: "2px 4px",
					borderRadius: "3px",
					border: "1px solid #15803d",
					fontSize: "12px",
				};
			}

			return {
				backgroundColor: "transparent",
				color: "inherit",
				padding: "2px 4px",
				borderRadius: "3px",
				fontWeight: "normal",
				fontSize: "12px",
			};
		}; // User access control
		// useMemo ensures the email value is stable across renders
		const userEmail = useMemo(
			() => (getUserEmail ? getUserEmail() : ""),
			[getUserEmail],
		);
		const {
			canWrite,
			isLoading: accessLoading,
			refreshAccess,
		} = useUserAccess(userEmail);

		const [data, setData] = useState([]);
		const [groupedData, setGroupedData] = useState([]);
		const [loading, setLoading] = useState(true);
		const [error, setError] = useState(null);
		const [editingItem, setEditingItem] = useState(null);
		const [editData, setEditData] = useState({
			total_req: "",
			series_req: "",
			se_teams: "",
			purchase_type: "",
			supplier: "",
			tool_req: "",
		});
		const [originalEditData, setOriginalEditData] = useState({});
		const [editingItemType, setEditingItemType] = useState(null);
		const [saving, setSaving] = useState(false);
		const [allSelected, setAllSelected] = useState(false);
		const [rangePopoverOpen, setRangePopoverOpen] = useState(false);
		const [rangeFrom, setRangeFrom] = useState("");
		const [rangeTo, setRangeTo] = useState("");
		const [rangeError, setRangeError] = useState("");
		const rangePopoverRef = useRef(null);
		const checkboxRef = useRef(null);
		const [currentPage, setCurrentPage] = useState(1);
		const [itemsPerPage, setItemsPerPage] = useState(100);
		const [isLoading, setIsLoading] = useState(true);
		const [isLoadingMore, setIsLoadingMore] = useState(false);
		const [hasMoreData, setHasMoreData] = useState(true);
		const [commentModalOpen, setCommentModalOpen] = useState(false);
		const [currentSnr, setCurrentSnr] = useState(null);
		const [currentItemType, setCurrentItemType] = useState(null);
		const [newComment, setNewComment] = useState("");
		const [editingCommentId, setEditingCommentId] = useState(null);
		const [editText, setEditText] = useState("");
		const [totalRecords, setTotalRecords] = useState(0);
		const [totalPages, setTotalPages] = useState(1);
		const [currentFilters, setCurrentFilters] = useState({});
		const [initialDataLoaded, setInitialDataLoaded] = useState(false);
		const [flagLoadingItems, setFlagLoadingItems] = useState(new Set());
		const [showWarningModal, setShowWarningModal] = useState(false);
		const [showAccessDeniedModal, setShowAccessDeniedModal] = useState(false);
		const [isAnyFlagUpdating, setIsAnyFlagUpdating] = useState(false);
		const [lastDbUpdate, setLastDbUpdate] = useState(null);
		const dbUpdateFetched = useRef(false);
		// Followup date editing state (per-row, keyed by composite item key)
		const [openFollowupDatePicker, setOpenFollowupDatePicker] = useState(null);
		const [followupDates, setFollowupDates] = useState({});
		const [updatingFollowupItems, setUpdatingFollowupItems] = useState({});

		// Side panel state
		const [sidePanelOpen, setSidePanelOpen] = useState(false);
		const [selectedSidePanelRow, setSelectedSidePanelRow] = useState(null);

		// Create a ref to access the table methods
		const dataTableRef = useRef({});
		React.useImperativeHandle(ref, () => ({
			fetchData,
			fetchInitialData,
			resetInitialDataFlag: () => setInitialDataLoaded(false), // Add method to reset the flag
			setInitialDataLoaded: () => setInitialDataLoaded(true), // Add method to set the flag
			getCurrentPage: () => currentPage,
			getItemsPerPage: () => itemsPerPage,
			getAllData: () => data,
			// Patch specific rows in-place — no fetchData, no scroll reset.
			// updatedItems: array of selected items; updates: field→value map from bulk edit.
			updateLocalData: (updatedItems, updates) => {
				// Build a Set of composite keys for O(1) lookup
				const keySet = new Set(
					updatedItems.map(
						(i) =>
							`${i.snr_kurz || i.snr || ""}-${i.pt_snr || i.ptsnr || ""}-${i.projekt || i.project || ""}`,
					),
				);
				setData((prev) =>
					prev.map((item) => {
						const itemKey = `${item.snr_kurz || item.snr || ""}-${item.pt_snr || item.ptsnr || ""}-${item.projekt || item.project_no || item.project || ""}`;
						if (!keySet.has(itemKey)) return item;
						return { ...item, ...updates };
					}),
				);
				// Also update groupedData for the "Both" view
				setGroupedData((prev) =>
					prev.map((group) => {
						const protoKey = group.prototype
							? `${group.prototype.snr_kurz || group.prototype.snr || ""}-${group.prototype.pt_snr || group.prototype.ptsnr || ""}-${group.prototype.projekt || group.prototype.project || ""}`
							: null;
						const vffKey = group.vff
							? `${group.vff.snr_kurz || group.vff.snr || ""}-${group.vff.pt_snr || group.vff.ptsnr || ""}-${group.vff.projekt || group.vff.project || ""}`
							: null;
						const protoMatch = protoKey && keySet.has(protoKey);
						const vffMatch = vffKey && keySet.has(vffKey);
						if (!protoMatch && !vffMatch) return group;
						return {
							...group,
							prototype: protoMatch
								? { ...group.prototype, ...updates }
								: group.prototype,
							vff: vffMatch ? { ...group.vff, ...updates } : group.vff,
						};
					}),
				);
				console.log(
					`✅ PTSachNrDataTable: Patched ${updatedItems.length} rows in-place (no scroll reset)`,
				);
			},
			clearSelections: () => {
				// Clear any internal selection state if needed
				if (onSelectItems) {
					onSelectItems([]);
				}
			},
		}));

		// Function to process data for Both view - group by SNR and Project
		const processDataForBothView = useCallback((rawData) => {
			console.log("Raw data received for processing:", rawData);
			if (!rawData || rawData.length === 0) return [];

			// Group items by SNR and Project number
			const grouped = rawData.reduce((acc, item) => {
				const key = `${item.snr}_${item.project}`;

				if (!acc[key]) {
					acc[key] = {
						snr: item.snr,
						project: item.project,
						description: item.description,
						prototype: null,
						vff: null,
						combinedData: {
							// Use data from whichever source is available, prioritizing prototype
							ramp_up_group: item.ramp_up_group,
							se_teams: item.se_teams,
							purchase_type: item.purchase_type,
							purchase_type_system: item.purchase_type_system,
							supplier: item.supplier,
						},
					};
				}
				// Determine if this is prototype or VFF data based on source_type field from backend
				// Check for both uppercase and lowercase versions for compatibility
				if (
					item.source_type === "Prototype" ||
					item.source_type === "prototype"
				) {
					acc[key].prototype = item;
				} else if (item.source_type === "VFF" || item.source_type === "vff") {
					acc[key].vff = item;
				} else {
					// Fallback: determine based on available fields if source_type is missing
					if (
						item.ptsnr !== undefined ||
						(item.total_req !== undefined && item.series_req === undefined)
					) {
						acc[key].prototype = item;
					} else if (
						item.vmsnr !== undefined ||
						item.series_req !== undefined ||
						item.tool_req !== undefined
					) {
						acc[key].vff = item;
					} else if (item.category === "prototype") {
						// Use the new category field if available
						acc[key].prototype = item;
					} else if (item.category === "vff") {
						acc[key].vff = item;
					} else {
						// Default to prototype if we can't determine
						acc[key].prototype = item;
					}
				}

				// Update combined data with any newer information
				Object.keys(acc[key].combinedData).forEach((field) => {
					if (item[field] && item[field] !== "-") {
						acc[key].combinedData[field] = item[field];
					}
				});

				return acc;
			}, {});

			// Convert to array and sort by SNR
			const result = Object.values(grouped).sort((a, b) => {
				if (a.snr !== b.snr) return a.snr.localeCompare(b.snr);
				return a.project.localeCompare(b.project);
			});

			console.log("Processed grouped data:", result);
			return result;
		}, []);
		const requestCache = useRef({});

		// Fetch last DB update timestamp on mount
		useEffect(() => {
			if (dbUpdateFetched.current) return;
			dbUpdateFetched.current = true;
			apiClient
				.get("/api/ptsachnr-last-db-update")
				.then((response) => {
					if (response.data && response.data.last_update) {
						setLastDbUpdate(response.data.last_update);
					}
				})
				.catch((error) => {
					console.error("❌ Error fetching PTSachNr last DB update:", error);
					dbUpdateFetched.current = false;
				});
		}, []);
		const inFlightRequests = useRef({});
		const lastRequestTime = useRef({});

		// Infinite scroll handler similar to VFFDataTable
		const handleScroll = React.useCallback(
			(e) => {
				if (isLoadingMore || !hasMoreData || isLoading) return;

				const container = e.target;
				const scrollTop = container.scrollTop;
				const scrollHeight = container.scrollHeight;
				const clientHeight = container.clientHeight;

				// Only proceed if there's actually scrollable content
				if (scrollHeight <= clientHeight) return;

				// Trigger load more when user scrolls to within 200px of bottom
				const threshold = 200;
				const remainingScroll = scrollHeight - scrollTop - clientHeight;

				if (remainingScroll < threshold) {
					console.log("🔄 Infinite scroll triggered - Loading more data...", {
						scrollTop,
						scrollHeight,
						clientHeight,
						remaining: remainingScroll,
						threshold,
						currentPage,
						hasMoreData,
					});

					const nextPage = currentPage + 1;
					setCurrentPage(nextPage);
					// Keep consistent page size for proper pagination
					fetchData(
						currentFilters,
						nextPage,
						itemsPerPage,
						selectedViewType,
						false,
						false,
						true,
					);
				}
			},
			[
				isLoadingMore,
				hasMoreData,
				isLoading,
				currentPage,
				currentFilters,
				selectedViewType,
				itemsPerPage,
			],
		);

		// Load more data function for manual trigger (optional)
		const loadMoreData = React.useCallback(() => {
			if (isLoadingMore || !hasMoreData || isLoading) return;

			const nextPage = currentPage + 1;
			setCurrentPage(nextPage);
			// Keep consistent page size for proper pagination
			fetchData(
				currentFilters,
				nextPage,
				itemsPerPage,
				selectedViewType,
				false,
				false,
				true,
			);
		}, [
			isLoadingMore,
			hasMoreData,
			isLoading,
			currentPage,
			currentFilters,
			selectedViewType,
			itemsPerPage,
		]);
		const fetchData = useCallback(
			async (
				filters = {},
				page = 1,
				perPage = itemsPerPage,
				viewType = "Prototype",
				forceRefresh = false,
				useCache = false,
				isLoadMore = false,
			) => {
				const validPerPage = perPage;
				console.log(
					"PTSachNrDataTable fetchData called with viewType:",
					viewType,
					"and filters:",
					filters,
					"isLoadMore:",
					isLoadMore,
				);

				const cacheKey = `${JSON.stringify(filters)}_${page}_${validPerPage}_${viewType}`;

				// Prevent duplicate requests within 100ms — check BEFORE setting loading state
				// so a blocked call never leaves isLoading stuck as true.
				const now = Date.now();
				if (
					lastRequestTime.current[cacheKey] &&
					now - lastRequestTime.current[cacheKey] < 100 &&
					!forceRefresh
				) {
					console.log("🚫 BLOCKED DUPLICATE REQUEST within 100ms:", cacheKey);
					return Promise.resolve();
				}
				lastRequestTime.current[cacheKey] = now;

				if (!isLoadMore) {
					setIsLoading(true);
					setError(null);
				} else {
					setIsLoadingMore(true);
				} // If the same request is already in flight, just wait for it — the
				// caller already set isLoading(true) above, and the original promise's
				// .finally() will call setIsLoading(false) when it finishes.
				if (inFlightRequests.current[cacheKey] && !forceRefresh) {
					// Attach our own cleanup so loading is always cleared even for the
					// "piggy-back" caller that is reusing the in-flight promise.
					return inFlightRequests.current[cacheKey].then(
						(result) => {
							if (!isLoadMore) setIsLoading(false);
							else setIsLoadingMore(false);
							return result;
						},
						(err) => {
							if (!isLoadMore) setIsLoading(false);
							else setIsLoadingMore(false);
							throw err;
						},
					);
				}

				// Check cache first if not forcing a refresh.
				// For filter operations we intentionally skip the memory cache so that
				// re-applying a previous filter combo always hits the network.  The
				// memory cache is only useful for the very first page-load (useCache=true).
				const cachedResponse =
					useCache && !forceRefresh ? requestCache.current[cacheKey] : null;
				if (cachedResponse) {
					// Only update state if the data is different
					if (JSON.stringify(cachedResponse.data) !== JSON.stringify(data)) {
						setData(cachedResponse.data);

						// Process data for Both view
						if (viewType === "Both") {
							const processedData = processDataForBothView(cachedResponse.data);
							setGroupedData(processedData);
						} else {
							setGroupedData([]); // Clear grouped data for other views
						}
						setTotalRecords(cachedResponse.pagination.total);
						setTotalPages(cachedResponse.pagination.total_pages);
						setCurrentPage(cachedResponse.pagination.page);
						setHasMoreData(
							cachedResponse.pagination.page <
								cachedResponse.pagination.total_pages,
						);
					}
					// Always clear loading state when serving from cache
					if (!isLoadMore) setIsLoading(false);
					else setIsLoadingMore(false);
					return Promise.resolve(cachedResponse);
				} // Construct the payload for the POST request
				const payload = {
					project: filters.project || [],
					programm: filters.programm || [],
					purchase_type: filters.purchaseType || [],
					purchase_type_system: filters.purchaseTypeSystem || [],
					se_teams: filters.seTeams || [], // © yasshsakhare
					ramp_up_group: filters.rampUpGroup || [],
					supplier: filters.supplier || [],
					flag: filters.flag || [],
					aenderungsnummer: filters.aenderungsnummer || [],
					change_description: filters.changeDescription || [],
					fet_from: filters.fet?.from || "",
					fet_to: filters.fet?.to || "",
					dept: filters.dept || [],
					part_no_prefix: filters.partNoPrefix || [],
					search: filters.search || "",
					page: page,
					items_per_page: validPerPage,
					view_type: viewType,
					show_mismatch_only: !!filters.showMismatchOnly,
				};

				// Add logging to track requests with unique ID
				const requestId = Math.random().toString(36).substr(2, 9);
				console.log("🔍 FRONTEND REQUEST:", {
					requestId,
					page,
					items_per_page: validPerPage,
					filters: {
						project: payload.project,
						programm: payload.programm,
						purchase_type: payload.purchase_type,
						se_teams: payload.se_teams,
					},
					timestamp: new Date().toISOString(),
					forceRefresh,
				});

				const controller = new AbortController();
				const signal = controller.signal;

				const promise = new Promise((resolve, reject) => {
					// Set loading state only if we don't have cached data
					if (!cachedResponse) {
						setLoading(true);
					}

					setCurrentFilters(filters);

					const token = localStorage.getItem("idToken");

					if (!token) {
						console.error("No ID token found!");
						return;
					}

					apiClient
						.post("/api/filtered-data", payload, {
							signal,
							headers: {
								Accept: "application/json",
								"Cache-Control": "no-cache",
							},
						})
						.then((response) => {
							const result = response.data;
							// Only update state if the component is still mounted
							if (!signal.aborted) {
								requestCache.current[cacheKey] = result;

								if (isLoadMore) {
									// Append new data to existing data for infinite scroll
									console.log(
										`📄 Appending ${result.data.length} new records to existing ${data.length} records`,
									);
									setData((prevData) => [...prevData, ...result.data]);

									// Process data for Both view
									if (viewType === "Both") {
										const processedData = processDataForBothView(result.data);
										setGroupedData((prevData) => [
											...prevData,
											...processedData,
										]);
									}

									setHasMoreData(page < result.pagination.total_pages);
									console.log(
										`🔍 Infinite scroll status: page ${page}/${result.pagination.total_pages}, hasMore: ${page < result.pagination.total_pages}`,
									);
								} else {
									// Replace data for new search/filter
									console.log(
										`🔄 Loading fresh data: ${result.data.length} records, total pages: ${result.pagination.total_pages}`,
									);
									setData(result.data);

									// Process data for Both view
									if (viewType === "Both") {
										const processedData = processDataForBothView(result.data);
										setGroupedData(processedData);
									} else {
										setGroupedData([]); // Clear grouped data for other views
									}
									setHasMoreData(result.pagination.total_pages > 1);
								}

								setTotalRecords(result.pagination.total);
								setTotalPages(result.pagination.total_pages);
								setCurrentPage(result.pagination.page);
							}
							resolve(result);
						})
						.catch((error) => {
							if (error.name !== "AbortError") {
								console.error("Error in fetchData:", error);

								// Provide user-friendly error messages
								let userFriendlyMessage = "Failed to connect to server";

								if (error.message === "Failed to fetch") {
									userFriendlyMessage =
										"Network connection failed - please check your internet connection";
								} else if (error.message.includes("timeout")) {
									userFriendlyMessage =
										"Request timed out - server is taking too long to respond";
								} else if (error.message.includes("500")) {
									userFriendlyMessage =
										"Server error - please try again in a moment";
								} else if (error.message.includes("404")) {
									userFriendlyMessage =
										"Data endpoint not found - please contact support";
								} else if (
									error.message.includes("403") ||
									error.message.includes("401")
								) {
									userFriendlyMessage =
										"Access denied - please check your permissions";
								}
								// Only set error after a short delay to avoid flashing error screens
								setTimeout(() => {
									if (!signal.aborted) {
										setError(userFriendlyMessage);
										if (!isLoadMore) {
											setData([]);
											setTotalRecords(0);
											setTotalPages(1);
											setHasMoreData(false);
										}
									}
								}, 1000);

								reject(error);
							}
						})
						.finally(() => {
							if (!signal.aborted) {
								if (!isLoadMore) {
									setIsLoading(false);
								} else {
									setIsLoadingMore(false);
								}
							}
							delete inFlightRequests.current[cacheKey];
						});
				});

				// Store the abort controller with the promise
				promise.cancel = () => controller.abort();
				inFlightRequests.current[cacheKey] = promise;

				return promise;
			},
			[itemsPerPage],
		);
		const fetchInitialData = useCallback(
			(customItemsPerPage = null) => {
				const perPage = customItemsPerPage || itemsPerPage;

				// Update itemsPerPage if customItemsPerPage is provided and different
				if (customItemsPerPage && itemsPerPage !== customItemsPerPage) {
					setItemsPerPage(customItemsPerPage);
				}

				console.log(
					`Fetching initial ${selectedViewType} data with ${perPage} items per page using cache`,
				);

				// Call fetchData with useCache=true to try loading from cache first
				// But skip cache if coming from dashboard (preventInitialLoad = true)
				const shouldUseCache = !preventInitialLoad;
				fetchData(
					{},
					1,
					perPage,
					selectedViewType,
					false,
					shouldUseCache,
					false,
				)
					.then(() => {
						setInitialDataLoaded(true);
					})
					.catch((error) => {
						console.error("Error fetching initial data:", error);
						setError(`Failed to load initial data: ${error.message}`);
					});
			},
			[fetchData, itemsPerPage, selectedViewType, preventInitialLoad],
		);

		// Use a ref so this effect fires exactly once on mount (or when preventInitialLoad changes).
		// We deliberately do NOT include fetchInitialData or selectedViewType in the dependency array
		// because those change on every filter/itemsPerPage update and would re-trigger an initial
		// load mid-flight — causing the "endless loading after every second filter" bug.
		const initialLoadFiredRef = useRef(false);
		useEffect(() => {
			if (!preventInitialLoad && !initialLoadFiredRef.current) {
				initialLoadFiredRef.current = true;
				fetchInitialData();
			}
		}, [preventInitialLoad]); // eslint-disable-line react-hooks/exhaustive-deps

		useEffect(() => {
			if (editOptions && Object.keys(editOptions).length > 0) {
				console.log("Edit options updated:", {
					setAllocations: editOptions.seTeams?.length || 0,
					purchaseTypes: editOptions.purchaseTypes?.length || 0,
				});
			}
		}, [editOptions]);
		// Handle select-all checkbox click — open range popover instead of immediately selecting
		const handleSelectAllClick = (e) => {
			e.preventDefault();
			setRangeFrom("");
			setRangeTo("");
			setRangeError("");
			setRangePopoverOpen((prev) => !prev);
		};

		// Apply range selection (uses `data` for Prototype/VFF/Series, `groupedData` for Both)
		const handleApplyRangeSelection = () => {
			const sourceData =
				selectedViewType === "Both"
					? groupedData.map(
							(g) => g.prototype || g.vff || { snr: g.snr, project: g.project },
						)
					: data;
			const from = parseInt(rangeFrom, 10);
			const to = parseInt(rangeTo, 10);
			const total = sourceData.length;

			if (!rangeFrom && !rangeTo) {
				onSelectItems(sourceData);
				setAllSelected(true);
				setRangePopoverOpen(false);
				return;
			}

			if (isNaN(from) || isNaN(to)) {
				setRangeError("Please enter valid numbers for both fields.");
				return;
			}
			if (from < 1 || to < 1) {
				setRangeError("Row numbers must be 1 or greater.");
				return;
			}
			if (from > to) {
				setRangeError('"From" must be less than or equal to "To".');
				return;
			}
			if (from > total) {
				setRangeError(`"From" (${from}) exceeds total loaded rows (${total}).`);
				return;
			}

			const clampedTo = Math.min(to, total);
			const rangeItems = sourceData.slice(from - 1, clampedTo);

			// Merge with existing selections, no duplicates
			const existingKeys = new Set(selectedItems.map((s) => getItemKey(s)));
			const newItems = rangeItems.filter(
				(item) => !existingKeys.has(getItemKey(item)),
			);
			const merged = [...selectedItems, ...newItems];

			onSelectItems(merged);
			setAllSelected(merged.length === total && total > 0);
			setRangeError("");
			setRangePopoverOpen(false);
		};

		// Close popover when clicking outside
		useEffect(() => {
			if (!rangePopoverOpen) return;
			const handleOutsideClick = (e) => {
				if (
					rangePopoverRef.current &&
					!rangePopoverRef.current.contains(e.target) &&
					checkboxRef.current &&
					!checkboxRef.current.contains(e.target)
				) {
					setRangePopoverOpen(false);
				}
			};
			document.addEventListener("mousedown", handleOutsideClick);
			return () =>
				document.removeEventListener("mousedown", handleOutsideClick);
		}, [rangePopoverOpen]);

		// Build a unique composite key per row: snr_kurz + ptsnr + project.
		// Using only snr caused phantom multi-selection when several rows share the
		// same snr_kurz (e.g. same part in different projects) or when snr is null/empty.
		const getItemKey = (item) =>
			`${item.snr_kurz || item.snr || ""}-${item.pt_snr || item.ptsnr || ""}-${item.projekt || item.project || ""}`;

		const handleSelectItem = (item) => {
			const key = getItemKey(item);
			const isSelected = selectedItems.some(
				(selected) => getItemKey(selected) === key,
			);
			if (isSelected) {
				onSelectItems(
					selectedItems.filter((selected) => getItemKey(selected) !== key),
				);
			} else {
				onSelectItems([...selectedItems, item]);
			}
		};
		// Update allSelected state based on current data and selection
		useEffect(() => {
			if (selectedViewType === "Both") {
				const relevantData = groupedData.map(
					(groupedItem) =>
						groupedItem.prototype ||
						groupedItem.vff || {
							snr: groupedItem.snr,
							project: groupedItem.project,
						},
				);
				const allItemsSelected =
					relevantData.length > 0 &&
					relevantData.every((item) =>
						selectedItems.some(
							(selected) => getItemKey(selected) === getItemKey(item),
						),
					);
				setAllSelected(allItemsSelected);
			} else {
				const allItemsSelected =
					data.length > 0 &&
					data.every((item) =>
						selectedItems.some(
							(selected) => getItemKey(selected) === getItemKey(item),
						),
					);
				setAllSelected(allItemsSelected);
			}
		}, [selectedItems, data, groupedData, selectedViewType]);

		// Clean up edit states when view type changes or component unmounts
		useEffect(() => {
			// Cancel any ongoing edits when view type changes
			if (editingItem) {
				handleCancelEdit();
			}
			// Close comment modal when view type changes
			if (commentModalOpen) {
				handleCloseCommentModal();
			}
		}, [selectedViewType]);
		const handleOpenCommentModal = (item, itemType = null) => {
			// Cancel any ongoing edit operations to prevent state conflicts
			if (editingItem) {
				handleCancelEdit();
			}

			// Use pt_snr as the identifier for comments (backend expects ptsnr)
			setCurrentSnr(item.pt_snr || item.ptsnr);
			setCurrentItemType(itemType); // Set the item type (prototype/vff)
			setEditingItem(null);
			setEditData({
				se_teams: item.se_teams || "",
				total_req: item.total_req || "",
				purchase_type: item.purchase_type || "",
				supplier: item.supplier || "",
			});
			// Reset comment modal states when opening for a new record
			setNewComment("");
			setEditingCommentId(null);
			setEditText("");
			setCommentModalOpen(true);
		};
		const handleEdit = (item, itemType = null) => {
			// Prevent edits for read-only users
			if (!canWrite) return;

			// Create unique identifier using composite key: snr_kurz + pt_snr + projekt
			const newUniqueId = `${item.snr_kurz || item.snr}-${item.pt_snr}-${item.projekt || item.project_no || item.project}${itemType ? `-${itemType}` : ""}`;
			// If we're already editing a different item, cancel the previous edit
			if (editingItem && editingItem !== newUniqueId) {
				setEditData({
					total_req: "",
					series_req: "",
					se_teams: "",
					purchase_type: "",
					supplier: "",
					tool_req: "",
				});
				setOriginalEditData({});
			}

			setEditingItem(newUniqueId);
			setEditingItemType(itemType); // Store the type being edited ('prototype' or 'vff')
			const editValues = {
				se_teams: item.se_teams || "",
				total_req: item.total_req || "",
				series_req: item.series_req || "",
				purchase_type: item.purchase_type || "",
				supplier: item.supplier || "",
				tool_req: item.tool_req || "",
			};
			setEditData(editValues);
			setOriginalEditData({ ...editValues }); // Store original values for comparison
		}; // Helper function to check if an item is being edited
		const isItemBeingEdited = (item, itemType = null) => {
			const itemUniqueId = `${item.snr_kurz || item.snr}-${item.pt_snr}-${item.projekt || item.project_no || item.project}${itemType ? `-${itemType}` : ""}`;
			return editingItem === itemUniqueId;
		};
		const handleCancelEdit = () => {
			setEditingItem(null);
			setEditingItemType(null);
			// Reset to empty state instead of keeping old data
			setEditData({
				total_req: "",
				series_req: "",
				se_teams: "",
				purchase_type: "",
				supplier: "",
				tool_req: "",
			});
			setOriginalEditData({});
		};
		const handleSave = async () => {
			if (!editingItem) return;
			if (!canWrite) return;

			setSaving(true);
			try {
				// Determine which item is being edited based on the editing state
				let itemToUpdate = null;

				if (selectedViewType === "Both") {
					// In Both view, find the item from grouped data
					const groupedItem = groupedData.find((group) => {
						const prototypeId = `${group.snr_kurz || group.snr}-${group.pt_snr}-${group.projekt || group.project}-prototype`;
						const vffId = `${group.snr_kurz || group.snr}-${group.pt_snr}-${group.projekt || group.project}-vff`;
						return editingItem === prototypeId || editingItem === vffId;
					});

					if (groupedItem) {
						if (editingItemType === "prototype" && groupedItem.prototype) {
							itemToUpdate = groupedItem.prototype;
						} else if (editingItemType === "vff" && groupedItem.vff) {
							itemToUpdate = groupedItem.vff;
						}
					}
				} else {
					// For single view types, find from regular data array using composite key
					itemToUpdate = data.find((item) => {
						const itemUniqueId = `${item.snr_kurz || item.snr}-${item.pt_snr}-${item.projekt || item.project_no || item.project}`;
						return (
							itemUniqueId ===
							editingItem.replace("-prototype", "").replace("-vff", "")
						);
					});
				}

				if (!itemToUpdate) {
					throw new Error("Item not found for update");
				}

				// Prepare the update request - only include changed values
				console.log("🔍 Debug - itemToUpdate:", itemToUpdate);
				console.log("🔍 Debug - selectedViewType:", selectedViewType);
				console.log("🔍 Debug - editingItemType:", editingItemType);

				const updateRequest = {
					snr: itemToUpdate.snr_kurz || itemToUpdate.snr,
					ptsnr: itemToUpdate.pt_snr || itemToUpdate.ptsnr,
					project:
						itemToUpdate.projekt ||
						itemToUpdate.project ||
						itemToUpdate.project_no,
					view_type: editingItemType || selectedViewType, // Use the specific type being edited
				};

				// Only include fields that have actually changed from their original values
				Object.entries(editData).forEach(([key, value]) => {
					const originalValue = originalEditData[key] || "";
					const currentValue = value || "";

					// Only include if the value has actually changed
					if (currentValue !== originalValue) {
						updateRequest[key] = currentValue;
					}
				});

				// Check if any fields were actually changed
				const changedFields = Object.keys(updateRequest).filter(
					(key) => !["snr", "ptsnr", "project", "view_type"].includes(key),
				);

				if (changedFields.length === 0) {
					alert("No changes detected. Please modify a field before saving.");
					setSaving(false);
					return;
				}

				console.log(
					"📝 Sending update request for changed fields only:",
					updateRequest,
				);
				console.log("🔄 Changed fields:", changedFields); // Call the backend API to update the data
				const response = await apiClient.put(
					"/api/update-ptsachnr-data",
					updateRequest,
				);

				console.log("✅ Update successful:", response.data);

				// Auto-comment when BY SET (purchase_type) or SET Allocation (se_teams)
				// is changed. Fired in the background (not awaited) so it never blocks
				// the save — mirrors how the backend auto-comments Assembly Date updates.
				const autoCommentFields = [
					{ field: "purchase_type", label: "BY SET" },
					{ field: "se_teams", label: "SET Allocation" },
				].filter(({ field }) => changedFields.includes(field));

				if (autoCommentFields.length > 0) {
					const currentUser =
						(getUserDisplayName ? getUserDisplayName() : null) ||
						localStorage.getItem("username") ||
						"Current User";
					const ptsnr = itemToUpdate.pt_snr || itemToUpdate.ptsnr;
					// Derive the DB category: prefer editingItemType (already lowercase 'prototype'/'vff'),
					// then the item's own category field, then fall back to selectedViewType lowercased.
					const category =
						editingItemType ||
						itemToUpdate.category ||
						(selectedViewType !== "Both"
							? selectedViewType?.toLowerCase()
							: "prototype");

					autoCommentFields.forEach(({ field, label }) => {
						const oldVal = originalEditData[field] || "(empty)";
						const newVal = editData[field] || "(empty)";
						const commentText = `Updated the ${label} from "${oldVal}" to "${newVal}"`;
						// Don't await — let it run in the background and swallow errors so a
						// failed comment never blocks or breaks the field update.
						apiClient
							.post("/api/comments", {
								snr: ptsnr,
								text: commentText,
								user: currentUser,
								category,
							})
							.then(() =>
								console.log(`💬 Auto-comment added for ${label} change`),
							)
							.catch((commentErr) =>
								console.warn(
									`⚠️ Failed to auto-add ${label} comment:`,
									commentErr,
								),
							);
					});
				}

				// Update the local data state
				if (selectedViewType === "Both") {
					// Update grouped data
					const updatedGroupedData = groupedData.map((group) => {
						const prototypeId = `${group.snr_kurz || group.snr}-${group.pt_snr}-${group.projekt || group.project}-prototype`;
						const vffId = `${group.snr_kurz || group.snr}-${group.pt_snr}-${group.projekt || group.project}-vff`;

						if (editingItem === prototypeId && group.prototype) {
							return {
								...group,
								prototype: {
									...group.prototype,
									...editData,
									editHistory: [
										...(group.prototype.editHistory || []),
										{
											user: "Current User",
											timestamp: new Date().toISOString(),
											changes: Object.entries(editData)
												.filter(
													([key, value]) => group.prototype[key] !== value,
												)
												.map(
													([key, value]) =>
														`${key}: ${group.prototype[key] || "N/A"} → ${value}`,
												),
										},
									],
								},
							};
						} else if (editingItem === vffId && group.vff) {
							return {
								...group,
								vff: {
									...group.vff,
									...editData,
									editHistory: [
										...(group.vff.editHistory || []),
										{
											user: "Current User",
											timestamp: new Date().toISOString(),
											changes: Object.entries(editData)
												.filter(([key, value]) => group.vff[key] !== value)
												.map(
													([key, value]) =>
														`${key}: ${group.vff[key] || "N/A"} → ${value}`,
												),
										},
									],
								},
							};
						}
						return group;
					});
					setGroupedData(updatedGroupedData);
				} else {
					// Update regular data using composite key
					const updatedData = data.map((item) => {
						const itemUniqueId = `${item.snr_kurz || item.snr}-${item.pt_snr}-${item.projekt || item.project_no || item.project}`;
						const editingItemBase = editingItem
							.replace("-prototype", "")
							.replace("-vff", "");
						return itemUniqueId === editingItemBase
							? {
									...item,
									...editData,
									editHistory: [
										...(item.editHistory || []),
										{
											user: "Current User",
											timestamp: new Date().toISOString(),
											changes: Object.entries(editData)
												.filter(([key, value]) => item[key] !== value)
												.map(
													([key, value]) =>
														`${key}: ${item[key] || "N/A"} → ${value}`,
												),
										},
									],
								}
							: item;
					});
					setData(updatedData);
				}

				setEditingItem(null);
				setEditingItemType(null);
				setOriginalEditData({}); // Clear original data

				// Show success message
				// setTimeout(() => {
				//   alert(`✅ Success!\n\nData updated successfully for SNR: ${itemToUpdate.snr}\n\nChanged fields: ${changedFields.join(', ')}`);
				// }, 100);
			} catch (error) {
				console.error("❌ Error saving changes:", error);
				if (error?.response?.status === 403) {
					// Force re-check permissions — the token may have just been refreshed
					// so access might now be valid again
					refreshAccess();
					setShowAccessDeniedModal(true);
				} else {
					alert(`Error saving changes: ${error.message}`);
				}
			} finally {
				setSaving(false);
			}
		};

		const handleInputChange = (field, value) => {
			setEditData((prev) => ({
				...prev,
				[field]: value,
			}));
		};
		const handleCloseCommentModal = () => {
			setCommentModalOpen(false);
			setCurrentSnr(null);
			setCurrentItemType(null); // Reset item type
			// Reset all comment input states when closing modal
			setNewComment("");
			setEditingCommentId(null);
			setEditText("");
		};

		const handleCommentAdded = () => {
			// Trigger comment refresh in CommentModal
		};
		// Initialize dropdown options
		const [dropdownOptions, setDropdownOptions] = useState({
			setAllocations: editOptions.seTeams || [],
			purchaseTypes: editOptions.purchaseTypes || [],
			suppliers: [],
			rampUpGroups: [],
			bemiErfOptions: [],
			projectOptions: [],
		});
		useEffect(() => {
			setDropdownOptions({
				setAllocations: editOptions.seTeams || [],
				totalReq: editOptions.totalReq || [],
				purchaseTypes: editOptions.purchaseTypes || [],
				suppliers: editOptions.suppliers || [],
				rampUpGroups: editOptions.rampUpGroups || [],
				bemiErfOptions: editOptions.bemiErf || [],
				projectOptions: editOptions.projects || [],
			});
		}, [editOptions]);
		const { setAllocations, suppliers } = dropdownOptions;
		// Sort purchaseTypes: all regular values alphabetically, then Z, then NRV last.
		// For VFF view, ensure "M" is always present in the list.
		const purchaseTypes = [
			...new Set([
				...(dropdownOptions.purchaseTypes || []),
				...(selectedViewType === "VFF" ? ["M"] : []),
			]),
		].sort((a, b) => {
			const order = (v) => (v === "NRV" ? 2 : v === "Z" ? 1 : 0);
			const oa = order(a),
				ob = order(b);
			if (oa !== ob) return oa - ob;
			return a.localeCompare(b);
		});
		// in the backend /api/filtered-data endpoint. These are simple aliases.
		const displayData = data;
		const displayGroupedData = groupedData;

		// Calculate pagination based on whether we're using grouped data or regular data
		const currentDataLength =
			selectedViewType === "Both" ? displayGroupedData.length : displayData.length;
		const indexOfFirstItem = (currentPage - 1) * itemsPerPage + 1;
		const indexOfLastItem = Math.min(currentPage * itemsPerPage, totalRecords);
		const nextPage = () => {
			// For infinite scroll, this function now just loads more data
			if (hasMoreData && !isLoadingMore && !isLoading) {
				loadMoreData();
			}
		};

		const prevPage = () => {
			// For infinite scroll, previous page functionality is disabled
			// Users can scroll up to see previous records
			return;
		};
		const handleItemsPerPageChange = (newItemsPerPage) => {
			// This function is kept for compatibility but modified for infinite scroll
			// Fixed at 100 records per load
			setItemsPerPage(100);
			setCurrentPage(1);
			setData([]);
			setGroupedData([]);
			setHasMoreData(true);
			fetchData(currentFilters, 1, 100, selectedViewType, false, false, false)
				.then(() => {
					setTotalPages(Math.ceil(totalRecords / 100));
				})
				.catch((error) =>
					console.error("Error changing items per page:", error),
				);
		};

		// Handle status toggle functionality — 3-step cycle: null/blank → "in Arbeit" → "erledigt" → null/blank
		const handleFlagToggle = async (item) => {
			// Prevent edits for read-only users
			if (!canWrite) return;

			// Create a unique identifier for this item's flag toggle
			const itemId = `${item.snr}-${item.source_type || selectedViewType}-${item.category || ""}`;

			// Prevent multiple simultaneous updates for the same item
			if (flagLoadingItems.has(itemId)) {
				return;
			}

			// Check if any flag update is already in progress globally
			if (isAnyFlagUpdating) {
				setShowWarningModal(true);
				return;
			}

			try {
				// Set global flag updating state
				setIsAnyFlagUpdating(true);
				// Add item to loading state
				setFlagLoadingItems((prev) => new Set(prev).add(itemId));

				// 3-step cycle matching VFFDataTable behaviour
				let newFlagValue;
				switch (item.flag) {
					case null:
					case undefined:
					case "":
					case "N":
						newFlagValue = "in Arbeit";
						break;
					case "in Arbeit":
						newFlagValue = "erledigt";
						break;
					case "erledigt":
						newFlagValue = "__clear__"; // sentinel → backend sets flag = NULL
						break;
					default:
						newFlagValue = "__clear__";
						break;
				}

				console.log("🏷️ Toggling flag:", {
					snr: item.snr,
					currentFlag: item.flag,
					newFlag: newFlagValue,
				});
				// Prepare the update request
				const updateRequest = {
					snr: item.snr,
					ptsnr: item.ptsnr,
					project: item.project || item.project_no,
					flag: newFlagValue,
					view_type: selectedViewType,
					// Add category/source_type information for "Both" view to help backend identify table
					category:
						item.category ||
						(item.source_type && item.source_type.toLowerCase()),
					source_type: item.source_type,
				};

				console.log("🏷️ Toggling flag for item:", updateRequest);
				// Call the backend API to update the flag (Axios returns data in response.data)
				const response = await apiClient.put(
					"/api/update-ptsachnr-data",
					updateRequest,
				);

				// Axios throws on non-2xx, so if we're here it's successful
				const result = response.data;
				console.log("✅ Flag update successful:", result);

				// Update the local data state immediately
				console.log(
					"🔄 Updating local data state with new flag value:",
					newFlagValue,
				);
				// Store null locally when sentinel was used so the toggle shows "None"
				const localFlagValue =
					newFlagValue === "__clear__" ? null : newFlagValue;
				if (selectedViewType === "Both") {
					// Update grouped data - but only update the specific record type (prototype or vff) that was clicked
					const updatedGroupedData = groupedData.map((group) => {
						// Check if this is a prototype item being updated
						if (
							group.prototype &&
							group.prototype.snr === item.snr &&
							(item.source_type === "Prototype" ||
								item.category === "prototype" ||
								(!item.source_type && !item.category))
						) {
							return {
								...group,
								prototype: { ...group.prototype, flag: localFlagValue },
							};
						}
						// Check if this is a VFF item being updated
						if (
							group.vff &&
							group.vff.snr === item.snr &&
							(item.source_type === "VFF" || item.category === "vff")
						) {
							return {
								...group,
								vff: { ...group.vff, flag: localFlagValue },
							};
						}
						return group;
					});
					setGroupedData(updatedGroupedData);
				} else {
					// Update regular data
					const updatedData = data.map((dataItem) => {
						return dataItem.snr === item.snr
							? { ...dataItem, flag: localFlagValue }
							: dataItem;
					});
					setData(updatedData);
				}
			} catch (error) {
				console.error("❌ Error toggling flag:", error);
				if (error?.response?.status === 403) {
					refreshAccess();
					setShowAccessDeniedModal(true);
				} else {
					// Show user-friendly message for specific backend errors
					const serverMessage =
						error?.response?.data?.detail ||
						error?.response?.data?.message ||
						"";
					if (
						serverMessage.includes("ICEBERG_COMMIT_ERROR") ||
						serverMessage.includes("Validation error")
					) {
						setShowWarningModal(true);
					} else {
						alert(`Error updating flag: ${serverMessage || error.message}`);
					}
				}
			} finally {
				// Reset global flag updating state
				setIsAnyFlagUpdating(false);
				// Remove item from loading state
				setFlagLoadingItems((prev) => {
					const newSet = new Set(prev);
					newSet.delete(itemId);
					return newSet;
				});
			}
		};

		// Handle flagging icon toggle functionality (separate from Status flag toggle)
		const handleFlaggingToggle = async (item) => {
			if (!canWrite) return;

			const itemId = `flagging-${item.snr}-${item.source_type || selectedViewType}-${item.category || ""}`;
			if (flagLoadingItems.has(itemId)) return;
			if (isAnyFlagUpdating) {
				setShowWarningModal(true);
				return;
			}

			try {
				setIsAnyFlagUpdating(true);
				setFlagLoadingItems((prev) => new Set(prev).add(itemId));

				const newFlaggingValue = item.flagging === "Y" ? "N" : "Y";

				const updateRequest = {
					snr: item.snr,
					ptsnr: item.ptsnr,
					project: item.project || item.project_no,
					flagging: newFlaggingValue,
					view_type: selectedViewType,
					category:
						item.category ||
						(item.source_type && item.source_type.toLowerCase()),
					source_type: item.source_type,
				};

				const response = await apiClient.put(
					"/api/update-ptsachnr-data",
					updateRequest,
				);
				console.log("✅ Flagging update successful:", response.data);

				if (selectedViewType === "Both") {
					const updatedGroupedData = groupedData.map((group) => {
						if (
							group.prototype &&
							group.prototype.snr === item.snr &&
							(item.source_type === "Prototype" ||
								item.category === "prototype")
						) {
							return {
								...group,
								prototype: { ...group.prototype, flagging: newFlaggingValue },
							};
						}
						if (
							group.vff &&
							group.vff.snr === item.snr &&
							(item.source_type === "VFF" || item.category === "vff")
						) {
							return {
								...group,
								vff: { ...group.vff, flagging: newFlaggingValue },
							};
						}
						return group;
					});
					setGroupedData(updatedGroupedData);
				} else {
					const updatedData = data.map((dataItem) =>
						dataItem.snr === item.snr
							? { ...dataItem, flagging: newFlaggingValue }
							: dataItem,
					);
					setData(updatedData);
				}
			} catch (error) {
				console.error("❌ Error toggling flagging:", error);
				if (error?.response?.status === 403) {
					refreshAccess();
					setShowAccessDeniedModal(true);
				} else {
					const serverMessage =
						error?.response?.data?.detail ||
						error?.response?.data?.message ||
						"";
					if (
						serverMessage.includes("ICEBERG_COMMIT_ERROR") ||
						serverMessage.includes("Validation error")
					) {
						setShowWarningModal(true);
					} else {
						alert(`Error updating flagging: ${serverMessage || error.message}`);
					}
				}
			} finally {
				setIsAnyFlagUpdating(false);
				setFlagLoadingItems((prev) => {
					const s = new Set(prev);
					s.delete(itemId);
					return s;
				});
			}
		};

		// Handle followup date update — saves to supply_type_frontend_dev.followup_date.
		// Pass value=null to clear the date.
		const handleFollowupDateUpdate = async (item, value) => {
			if (!canWrite) {
				setShowAccessDeniedModal(true);
				return;
			}

			const key = getItemKey(item);
			if (updatingFollowupItems[key]) return;

			// Optimistic local update for immediate UI feedback
			const localValue = value || null;
			setFollowupDates((prev) => ({ ...prev, [key]: localValue }));
			setUpdatingFollowupItems((prev) => ({ ...prev, [key]: true }));

			try {
				const updateRequest = {
					snr: item.snr_kurz || item.snr,
					ptsnr: item.pt_snr || item.ptsnr,
					project: item.projekt || item.project || item.project_no,
					view_type: item.category || selectedViewType,
					category:
						item.category ||
						(item.source_type && item.source_type.toLowerCase()),
					source_type: item.source_type,
					followup_date: value === null ? "__clear__" : value,
				};

				const response = await apiClient.put(
					"/api/update-ptsachnr-data",
					updateRequest,
				);
				console.log("✅ Followup date update successful:", response.data);

				// Sync the value into data / groupedData so it persists across re-renders
				if (selectedViewType === "Both") {
					setGroupedData((prev) =>
						prev.map((group) => {
							const protoMatch =
								group.prototype && getItemKey(group.prototype) === key;
							const vffMatch = group.vff && getItemKey(group.vff) === key;
							if (!protoMatch && !vffMatch) return group;
							return {
								...group,
								prototype: protoMatch
									? { ...group.prototype, followup_date: localValue }
									: group.prototype,
								vff: vffMatch
									? { ...group.vff, followup_date: localValue }
									: group.vff,
							};
						}),
					);
				} else {
					setData((prev) =>
						prev.map((dataItem) =>
							getItemKey(dataItem) === key
								? { ...dataItem, followup_date: localValue }
								: dataItem,
						),
					);
				}
			} catch (error) {
				console.error("❌ Error updating followup date:", error);
				// Revert optimistic update
				setFollowupDates((prev) => ({
					...prev,
					[key]: item.followup_date || null,
				}));
				if (error?.response?.status === 403) {
					refreshAccess();
					setShowAccessDeniedModal(true);
				} else {
					alert(
						`Error updating followup date: ${error?.response?.data?.detail || error.message}`,
					);
				}
			} finally {
				setUpdatingFollowupItems((prev) => {
					const next = { ...prev };
					delete next[key];
					return next;
				});
			}
		};

		// Followup Date editable cell — text shows dd/MM/yyyy, pencil/calendar opens a
		// MUI DatePicker, X clears. Mirrors VFFDataTable's part_followup_date behaviour.
		const FollowupDateCell = ({ item }) => {
			const key = getItemKey(item);
			const isUpdating = !!updatingFollowupItems[key];
			const rawValue =
				followupDates[key] !== undefined
					? followupDates[key]
					: item.followup_date;

			const hasDate =
				rawValue && rawValue !== "-" && rawValue !== "N/A" && rawValue !== "";

			const displayValue = (() => {
				if (!hasDate) return "Select date";
				try {
					const dateObj = new Date(rawValue);
					if (!isNaN(dateObj.getTime())) {
						return format(dateObj, "dd/MM/yyyy");
					}
				} catch (e) {
					/* ignore */
				}
				return "Select date";
			})();

			return (
				<div
					style={{
						display: "flex",
						alignItems: "center",
						gap: "4px",
						position: "relative",
						justifyContent: "center",
					}}
				>
					<span style={{ fontSize: "11px", color: "#666", fontWeight: 600 }}>
						Date:
					</span>
					<div
						style={{
							display: "flex",
							alignItems: "center",
							gap: "4px",
							border: "1px solid #d1d5db",
							borderRadius: "4px",
							padding: "2px 6px",
							backgroundColor: "white",
							minWidth: "107px",
							justifyContent: "space-between",
						}}
					>
						<span
							style={{
								fontSize: "12px",
								color: hasDate ? "#333" : "#9ca3af",
								whiteSpace: "nowrap",
							}}
						>
							{displayValue}
						</span>
						{isUpdating ? (
							<CircularProgress size={12} sx={{ color: "#cc0000" }} />
						) : (
							<div
								style={{ display: "flex", alignItems: "center", gap: "2px" }}
							>
								<Calendar
									size={13}
									style={{
										color: canWrite ? "#1e2832" : "#ccc",
										cursor: canWrite ? "pointer" : "not-allowed",
									}}
									onClick={() => canWrite && setOpenFollowupDatePicker(key)}
								/>
								{hasDate && canWrite && (
									<X
										size={12}
										style={{ color: "#dc2626", cursor: "pointer" }}
										onClick={() => handleFollowupDateUpdate(item, null)}
										title="Clear date"
									/>
								)}
							</div>
						)}
						{openFollowupDatePicker === key && !isUpdating && canWrite && (
							<div
								style={{
									position: "absolute",
									zIndex: 20,
									backgroundColor: "white",
									padding: "8px",
									top: "100%",
									right: 0,
									boxShadow: "0 4px 16px rgba(0,0,0,0.2)",
								}}
							>
								<LocalizationProvider
									dateAdapter={AdapterDateFns}
									adapterLocale={enGB}
								>
									<DatePicker
										open={true}
										onClose={() => setOpenFollowupDatePicker(null)}
										slotProps={{
											textField: {
												size: "small",
												sx: {
													width: "140px",
													"& .MuiInputBase-root": {
														fontSize: "12px",
														height: "32px",
													},
												},
											},
											popper: {
												sx: {
													"& .MuiPaper-root": {
														"& .MuiPickersDay-root": {
															"&.Mui-selected": {
																backgroundColor: "#cc0000",
																color: "white",
																"&:hover": { backgroundColor: "#aa0000" },
															},
														},
														"& .MuiPickersDay-today": {
															border: "1px solid #cc0000",
														},
													},
												},
											},
										}}
										value={(() => {
											try {
												if (hasDate) {
													const d = new Date(rawValue);
													if (!isNaN(d.getTime())) return d;
												}
											} catch (e) {
												/* ignore */
											}
											return null;
										})()}
										onChange={(newDate) => {
											const formattedDate = newDate
												? formatDateForBackend(newDate)
												: null;
											handleFollowupDateUpdate(item, formattedDate);
											setOpenFollowupDatePicker(null);
										}}
									/>
								</LocalizationProvider>
							</div>
						)}
					</div>
				</div>
			);
		}; // Flag Toggle Component — 3-state: None → "in Arbeit" → "erledigt" → None
		const FlagToggle = ({ item }) => {
			const statusValue = item.flag;
			const itemId = `${item.snr}-${item.source_type || selectedViewType}-${item.category || ""}`;
			const isLoading = flagLoadingItems.has(itemId);

			// Resolve display info for each state
			const getStatusInfo = (val) => {
				switch (val) {
					case "in Arbeit":
						return {
							backgroundColor: "#f59e0b",
							borderColor: "#d97706",
							circlePos: 13, // middle
							label: "in Arbeit",
							textColor: "#d97706",
						};
					case "erledigt":
						return {
							backgroundColor: "#10b981",
							borderColor: "#059669",
							circlePos: 26, // right
							label: "erledigt",
							textColor: "#059669",
						};
					default:
						return {
							backgroundColor: "#e5e7eb",
							borderColor: "#d1d5db",
							circlePos: 2, // left
							label: "None",
							textColor: "#6b7280",
						};
				}
			};

			const info = getStatusInfo(statusValue);

			return (
				<div
					style={{
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						gap: "2px",
						width: "100%",
					}}
				>
					{/* Pill toggle */}
					<div
						onClick={(e) => {
							e.stopPropagation();
							if (!isLoading && canWrite) {
								handleFlagToggle(item);
							}
						}}
						style={{
							width: "42px",
							height: "18px",
							backgroundColor: info.backgroundColor,
							borderRadius: "9px",
							position: "relative",
							cursor: !canWrite || isLoading ? "not-allowed" : "pointer",
							transition: "all 0.2s ease",
							opacity: isLoading ? 0.7 : canWrite ? 1 : 0.6,
							border: `1px solid ${info.borderColor}`,
							flexShrink: 0,
							marginRight: "5px",
						}}
						title={
							!canWrite
								? `Status: ${info.label} (read-only)`
								: isLoading
									? "Updating..."
									: `Status: ${info.label} — click to cycle`
						}
					>
						<div
							style={{
								width: "12px",
								height: "12px",
								backgroundColor: "white",
								borderRadius: "50%",
								position: "absolute",
								top: "2px",
								left: `${info.circlePos}px`,
								transition: "left 0.2s ease",
								boxShadow: "0 1px 3px rgba(0,0,0,0.25)",
								border: `1px solid ${info.borderColor}`,
							}}
						/>
					</div>
					{/* Loading spinner — shown below the pill when updating */}
					{isLoading ? (
						<div
							style={{
								width: "10px",
								height: "10px",
								border: "2px solid #e5e7eb",
								borderTop: "2px solid #cc0000",
								borderRadius: "50%",
								animation: "spin 1s linear infinite",
							}}
						/>
					) : (
						<span
							style={{
								fontSize: "9px",
								color: info.textColor,
								fontWeight: 600,
								whiteSpace: "nowrap",
								lineHeight: 1,
								maxWidth: "42px",
								overflow: "hidden",
								textOverflow: "ellipsis",
								display: "block",
								textAlign: "center",
							}}
							title={info.label}
						>
							{info.label}
						</span>
					)}
				</div>
			);
		}; // Inline Flag Icon — orange when flagging=Y, grey outline when flagging=N, clickable to toggle
		const FlagIcon = ({ item }) => {
			const isY = item.flagging === "Y";
			const itemId = `flagging-${item.snr}-${item.source_type || selectedViewType}-${item.category || ""}`;
			const isFlagLoading = flagLoadingItems.has(itemId);

			return (
				<Tooltip
					title={
						!canWrite
							? `Flagging: ${item.flagging || "N"} (read-only)`
							: isFlagLoading
								? "Updating..."
								: isY
									? "Flagged — click to unflag"
									: "Not flagged — click to flag"
					}
					arrow
					placement="top"
				>
					<button
						onClick={(e) => {
							e.stopPropagation();
							if (canWrite && !isFlagLoading) {
								handleFlaggingToggle(item);
							}
						}}
						disabled={!canWrite || isFlagLoading}
						style={{
							background: "none",
							border: "none",
							padding: "2px",
							cursor: !canWrite || isFlagLoading ? "not-allowed" : "pointer",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							transition: "transform 0.15s ease",
						}}
						onMouseEnter={(e) => {
							if (canWrite && !isFlagLoading)
								e.currentTarget.style.transform = "scale(1.2)";
						}}
						onMouseLeave={(e) => {
							e.currentTarget.style.transform = "scale(1)";
						}}
					>
						{isFlagLoading ? (
							<CircularProgress
								size={13}
								thickness={5}
								sx={{ color: "#f97316" }}
							/>
						) : (
							<Flag
								size={15}
								style={{
									color: isY ? "#f97316" : "#d1d5db",
									fill: isY ? "#f97316" : "none",
									transition: "color 0.2s ease, fill 0.2s ease",
								}}
							/>
						)}
					</button>
				</Tooltip>
			);
		};

		if (isLoading || isDashboardLoading) {
			return (
				<Box
					display="flex"
					flexDirection="column"
					alignItems="center"
					justifyContent="center"
					height="100%"
					minHeight="300px"
				>
					<CircularProgress size={60} thickness={4} sx={{ color: "#cc0000" }} />
					<Typography
						variant="body1"
						style={{ marginTop: "20px", color: "#666" }}
					>
						{isDashboardLoading
							? "Applying dashboard filters..."
							: "We're preparing your content... Please hold"}
					</Typography>
				</Box>
			);
		}

		if (error) {
			return (
				<Box
					display="flex"
					flexDirection="column"
					alignItems="center"
					justifyContent="center"
					height="100%"
					minHeight="400px"
					padding="20px"
					sx={{
						backgroundColor: "#f8f9fa",
						border: "1px solid #e9ecef",
						borderRadius: "8px",
						margin: "20px",
					}}
				>
					<Box sx={{ textAlign: "center", maxWidth: "500px" }}>
						{/* Icon */}
						<Box
							sx={{
								width: "80px",
								height: "80px",
								margin: "0 auto 5px",
								borderRadius: "50%",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
							}}
						>
							<span style={{ fontSize: "40px" }}>⚠️</span>
						</Box>
						{/* Title */}
						<Typography
							variant="h5"
							sx={{
								fontFamily: "MAN EUROPE_Bold",
								color: "#1e2832",
								marginBottom: "12px",
								fontSize: "1.5rem",
							}}
						>
							Error Loading Data
						</Typography>
						{/* Message */}
						<Typography
							variant="body1"
							sx={{
								color: "#666",
								marginBottom: "24px",
								lineHeight: 1.6,
								fontFamily: "MAN EUROPE_Regular",
							}}
						>
							We're having trouble connecting to the server. This could be due
							to a network issue or the server might be temporarily unavailable.
						</Typography>
						{/* Refresh Button */}{" "}
						<button
							onClick={() => {
								setError(null);
								setIsLoading(true);
								// Retry with current filters
								fetchData(
									currentFilters,
									currentPage,
									itemsPerPage,
									selectedViewType,
								);
							}}
							style={{
								backgroundColor: "#cc0000",
								color: "white",
								border: "none",
								padding: "12px 24px",
								borderRadius: "6px",
								fontSize: "14px",
								fontFamily: "MAN EUROPE_Bold",
								cursor: "pointer",
								transition: "background-color 0.2s",
								marginRight: "12px",
							}}
							onMouseOver={(e) => (e.target.style.backgroundColor = "#b30000")}
							onMouseOut={(e) => (e.target.style.backgroundColor = "#cc0000")}
						>
							Refresh Data
						</button>
						{/* Secondary Action */}
						<button
							onClick={() => {
								setError(null);
								setIsLoading(true);
								// Try to fetch initial data
								fetchInitialData();
							}}
							style={{
								backgroundColor: "transparent",
								color: "#666",
								border: "1px solid #ddd",
								padding: "12px 24px",
								borderRadius: "6px",
								fontSize: "14px",
								fontFamily: "MAN EUROPE_Bold",
								cursor: "pointer",
								transition: "all 0.2s",
							}}
							onMouseOver={(e) => {
								e.target.style.backgroundColor = "#f8f9fa";
								e.target.style.borderColor = "#adb5bd";
							}}
							onMouseOut={(e) => {
								e.target.style.backgroundColor = "transparent";
								e.target.style.borderColor = "#ddd";
							}}
						>
							Reset Filters
						</button>
						{/* Technical Details (Collapsible) */}
						<details style={{ marginTop: "20px", textAlign: "left" }}>
							<summary
								style={{
									cursor: "pointer",
									color: "#666",
									fontSize: "13px",
									fontFamily: "MAN EUROPE_Regular",
								}}
							>
								Technical Details
							</summary>
							<Typography
								variant="caption"
								sx={{
									display: "block",
									marginTop: "8px",
									padding: "8px",
									backgroundColor: "#f8f9fa",
									border: "1px solid #e9ecef",
									borderRadius: "4px",
									fontFamily: "MAN EUROPE_Regular",
									fontSize: "12px",
									color: "#666",
								}}
							>
								{error}
							</Typography>
						</details>
					</Box>
				</Box>
			);
		}

		return (
			<>
				{/* Add CSS for spinner animation */}
				<style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>{" "}
				<CommentModal
					open={commentModalOpen}
					onClose={handleCloseCommentModal}
					snr={currentSnr}
					itemType={currentItemType}
					onAddComment={handleCommentAdded}
					newComment={newComment}
					setNewComment={setNewComment}
					editingCommentId={editingCommentId}
					setEditingCommentId={setEditingCommentId}
					editText={editText}
					setEditText={setEditText}
					getUserDisplayName={getUserDisplayName}
					canWrite={canWrite}
				/>{" "}
				<WarningModal
					open={showWarningModal}
					onClose={() => setShowWarningModal(false)}
				/>
				<AccessDeniedModal
					open={showAccessDeniedModal}
					onClose={() => setShowAccessDeniedModal(false)}
				/>
				<div
					style={{
						backgroundColor: "white",
						display: "flex",
						flexDirection: "column",
						height: "100%",
						position: "relative",
						overflow: "hidden",
					}}
					onScroll={handleScroll}
				>
					{" "}
					<div
						style={{
							overflowY: "auto",
							height: "100%",
							borderBottom: "1px solid #eee",
						}}
						onScroll={handleScroll}
					>
						<table
							style={{
								width: "100%",
								borderCollapse: "collapse",
								tableLayout: "fixed",
							}}
						>
							<colgroup>
								<col style={{ width: "45px" }} />
								{/* Serial Number */}
								<col style={{ width: "35px" }} />
								{/* Checkbox */}
								{selectedViewType === "Both" ? (
									// Both view: original single Details column
									<col style={{ width: "36%", minWidth: "280px" }} />
								) : (
									<>
										<col style={{ width: "calc(6%)" }} />
										{/* PTSNR */}
										<col style={{ width: "calc(13%)" }} />
										{/* Description */}
										<col style={{ width: "calc(4.5% )" }} />
										{/* Change Number */}
										<col style={{ width: "calc(13% - 10px)" }} />
										{/* Change No Description */}
										<col style={{ width: "calc(2%)" }} />
										{/* Fet */}
									</>
								)}
								{columnVisibility.rampUpGroup && (
									<col style={{ width: "calc(7%)" }} />
								)}
								{columnVisibility.seTeams && (
									<col
										style={{
											width:
												selectedViewType === "VFF" ||
												selectedViewType === "Series"
													? "calc(10% - 10px)"
													: "calc(8% - 10px)",
										}}
									/>
								)}
								<col
									style={{
										width: selectedViewType === "Both" ? "5%" : "calc(4%)",
									}}
								/>
								{/* Dept column */}
								{columnVisibility.purchaseType && (
									<col
										style={{
											width:
												selectedViewType === "VFF" ||
												selectedViewType === "Series"
													? "10%"
													: "8%",
										}}
									/>
								)}
								{selectedViewType === "Prototype" && (
									<col style={{ width: "calc(12% + 25px)" }} />
								)}
								{/* Series Annual Vol (Prototype total_req) */}
								{(selectedViewType === "VFF" ||
									selectedViewType === "Series" ||
									selectedViewType === "Both") && (
									<col
										style={{
											width:
												selectedViewType === "Both"
													? "calc(8% + 25px)"
													: "calc(12% + 25px)",
										}}
									/>
								)}
								{columnVisibility.supplier && (
									<col
										style={{
											width:
												selectedViewType === "Both"
													? "calc(5% + 15px)"
													: "calc(5% + 15px)",
										}}
									/>
								)}
								{/* Merged Status column (Actions + Date + Flag toggle) for all views */}
								<col
									style={{
										width: selectedViewType === "Both" ? "230px" : "230px",
										minWidth: "210px",
									}}
								/>
							</colgroup>
							<thead
								style={{
									backgroundColor: "#f8f9fa",
									position: "sticky",
									top: 0,
									zIndex: 1,
									boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
								}}
							>
								<tr>
									<th
										style={{
											padding: "12px",
											textAlign: "center",
											fontSize: "14px",
											color: "black",
											textTransform: "none",
											fontFamily: "MAN EUROPE_Bold",
											fontWeight: "500",
											width: "45px",
										}}
									>
										Sr No.
									</th>
									<th
										style={{
											padding: "12px",
											textAlign: "center",
											fontSize: "12px",
											fontWeight: "bold",
											color: "#666",
											textTransform: "none",
											width: "105px",
											position: "relative",
										}}
									>
										<div
											style={{
												position: "relative",
												display: "inline-flex",
												alignItems: "center",
											}}
										>
											<input
												ref={checkboxRef}
												type="checkbox"
												checked={allSelected}
												onChange={() => {}}
												onClick={handleSelectAllClick}
												className="select-all-checkbox selection-controls"
												style={{ cursor: "pointer" }}
												title="Click to open selection options (select all or pick a range)"
											/>
											{rangePopoverOpen && (
												<div
													ref={rangePopoverRef}
													style={{
														position: "absolute",
														top: "24px",
														left: "0",
														zIndex: 999,
														backgroundColor: "#fff",
														border: "1px solid #d1d5db",
														borderRadius: "8px",
														boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
														padding: "12px",
														width: "230px",
														fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
													}}
												>
													{/* Header */}
													<div
														style={{
															display: "flex",
															justifyContent: "space-between",
															alignItems: "center",
															marginBottom: "10px",
														}}
													>
														<span
															style={{
																fontSize: "12px",
																fontWeight: "600",
																color: "#111827",
																fontFamily: "MAN EUROPE_Bold",
															}}
														>
															Select Rows
														</span>
														<button
															onClick={() => setRangePopoverOpen(false)}
															style={{
																background: "none",
																border: "none",
																cursor: "pointer",
																padding: "0",
																color: "#6b7280",
																fontSize: "14px",
																lineHeight: 1,
															}}
															title="Close"
														>
															✕
														</button>
													</div>
													{/* Select All button */}
													<button
														onClick={() => {
															const sourceData =
																selectedViewType === "Both"
																	? groupedData.map(
																			(g) =>
																				g.prototype ||
																				g.vff || {
																					snr: g.snr,
																					project: g.project,
																				},
																		)
																	: data;
															onSelectItems(sourceData);
															setAllSelected(true);
															setRangePopoverOpen(false);
														}}
														style={{
															width: "100%",
															fontSize: "12px",
															padding: "6px 8px",
															backgroundColor: "#1d4ed8",
															color: "white",
															border: "none",
															borderRadius: "5px",
															cursor: "pointer",
															fontFamily: "MAN EUROPE_Bold",
															marginBottom: "10px",
														}}
													>
														{" "}
														Select All Loaded (
														{selectedViewType === "Both"
															? groupedData.length
															: data.length}
														)
													</button>
													{/* Deselect All button */}
													<button
														onClick={() => {
															onSelectItems([]);
															setAllSelected(false);
															setRangePopoverOpen(false);
														}}
														disabled={selectedItems.length === 0}
														style={{
															width: "100%",
															fontSize: "12px",
															padding: "6px 8px",
															backgroundColor:
																selectedItems.length === 0
																	? "#e5e7eb"
																	: "#dc3545",
															color:
																selectedItems.length === 0
																	? "#9ca3af"
																	: "white",
															border: "none",
															borderRadius: "5px",
															cursor:
																selectedItems.length === 0
																	? "not-allowed"
																	: "pointer",
															fontFamily: "MAN EUROPE_Bold",
															marginBottom: "10px",
														}}
													>
														Deselect All ({selectedItems.length})
													</button>
													{/* Divider */}
													<div
														style={{
															borderTop: "1px solid #e5e7eb",
															marginBottom: "10px",
														}}
													/>
													{/* Range label */}
													<div
														style={{
															fontSize: "11px",
															color: "#6b7280",
															marginBottom: "8px",
														}}
													>
														Or select by row number range:
													</div>
													{/* From / To inputs */}
													<div
														style={{
															display: "flex",
															gap: "8px",
															alignItems: "center",
															marginBottom: "8px",
														}}
													>
														<div style={{ flex: 1 }}>
															<label
																style={{
																	fontSize: "10px",
																	color: "#6b7280",
																	display: "block",
																	marginBottom: "2px",
																}}
															>
																From
															</label>
															<input
																type="number"
																min="1"
																max={
																	selectedViewType === "Both"
																		? groupedData.length
																		: data.length
																}
																value={rangeFrom}
																onChange={(e) => {
																	setRangeFrom(e.target.value);
																	setRangeError("");
																}}
																onKeyDown={(e) =>
																	e.key === "Enter" &&
																	handleApplyRangeSelection()
																}
																placeholder="e.g. 250"
																style={{
																	width: "100%",
																	padding: "5px 6px",
																	border: "1px solid #d1d5db",
																	borderRadius: "4px",
																	fontSize: "12px",
																	boxSizing: "border-box",
																	outline: "none",
																}}
															/>
														</div>
														<div style={{ flex: 1 }}>
															<label
																style={{
																	fontSize: "10px",
																	color: "#6b7280",
																	display: "block",
																	marginBottom: "2px",
																}}
															>
																To
															</label>
															<input
																type="number"
																min="1"
																max={
																	selectedViewType === "Both"
																		? groupedData.length
																		: data.length
																}
																value={rangeTo}
																onChange={(e) => {
																	setRangeTo(e.target.value);
																	setRangeError("");
																}}
																onKeyDown={(e) =>
																	e.key === "Enter" &&
																	handleApplyRangeSelection()
																}
																placeholder="e.g. 350"
																style={{
																	width: "100%",
																	padding: "5px 6px",
																	border: "1px solid #d1d5db",
																	borderRadius: "4px",
																	fontSize: "12px",
																	boxSizing: "border-box",
																	outline: "none",
																}}
															/>
														</div>
													</div>
													{rangeError && (
														<div
															style={{
																fontSize: "11px",
																color: "#dc2626",
																marginBottom: "8px",
																lineHeight: 1.4,
															}}
														>
															⚠ {rangeError}
														</div>
													)}
													<div
														style={{
															fontSize: "10px",
															color: "#9ca3af",
															marginBottom: "8px",
															lineHeight: 1.4,
														}}
													>
														Row numbers match the Sr No. column. Max loaded:{" "}
														{selectedViewType === "Both"
															? groupedData.length
															: data.length}
														.
													</div>
													<button
														onClick={handleApplyRangeSelection}
														style={{
															width: "100%",
															fontSize: "12px",
															padding: "6px 8px",
															backgroundColor: "#cc0000",
															color: "white",
															border: "none",
															borderRadius: "5px",
															cursor: "pointer",
															fontFamily: "MAN EUROPE_Bold",
														}}
													>
														Apply Range
													</button>
												</div>
											)}
										</div>
									</th>
									{selectedViewType === "Both" ? (
										// Both view: original single Details column
										<th
											style={{
												padding: "12px",
												textAlign: "center",
												fontSize: "14px",
												color: "black",
												textTransform: "none",
												fontFamily: "MAN EUROPE_Bold",
												fontWeight: "500",
											}}
										>
											Details
										</th>
									) : (
										<>
											<th
												style={{
													padding: "12px",
													textAlign: "center",
													fontSize: "14px",
													color: "black",
													textTransform: "none",
													fontFamily: "MAN EUROPE_Bold",
													fontWeight: "500",
												}}
											>
												{selectedViewType === "VFF"
													? "VMSNR"
													: selectedViewType === "Series"
														? "Series SNR"
														: "PTSNR"}
											</th>
											<th
												style={{
													padding: "12px",
													textAlign: "center",
													fontSize: "14px",
													color: "black",
													textTransform: "none",
													fontFamily: "MAN EUROPE_Bold",
													fontWeight: "500",
												}}
											>
												Part Description
											</th>
											<th
												style={{
													padding: "12px 16px",
													textAlign: "center",
													fontSize: "14px",
													color: "black",
													textTransform: "none",
													fontFamily: "MAN EUROPE_Bold",
													fontWeight: "500",
												}}
											>
												Change No
											</th>
											<th
												style={{
													padding: "12px 16px",
													textAlign: "center",
													fontSize: "14px",
													color: "black",
													textTransform: "none",
													fontFamily: "MAN EUROPE_Bold",
													fontWeight: "500",
												}}
											>
												Change Description
											</th>
											<th
												style={{
													padding: "12px",
													textAlign: "center",
													fontSize: "14px",
													color: "black",
													textTransform: "none",
													fontFamily: "MAN EUROPE_Bold",
													fontWeight: "500",
												}}
											>
												FET
											</th>
										</>
									)}
									{columnVisibility.rampUpGroup && (
										<th
											style={{
												padding: "12px",
												textAlign: "center",
												fontSize: "14px",
												color: "black",
												textTransform: "none",
												fontFamily: "MAN EUROPE_Bold",
												fontWeight: "500",
											}}
										>
											<div
												style={{
													display: "flex",
													alignItems: "center",
													justifyContent: "center",
													gap: "6px",
												}}
											>
												ALG
												<Tooltip
													title="The change numbers are grouped under ramp up groups and the parts numbers are grouped under change number. Parts under same ramp up group will be assembled together."
													arrow
												>
													<Info
														size={14}
														style={{
															color: "black",
															opacity: 0.7,
															transition: "opacity 0.2s",
														}}
														onMouseEnter={(e) => (e.target.style.opacity = "1")}
														onMouseLeave={(e) =>
															(e.target.style.opacity = "0.7")
														}
													/>
												</Tooltip>
											</div>
										</th>
									)}
									{columnVisibility.seTeams && (
										<th
											style={{
												padding: "12px",
												textAlign: "center",
												fontSize: "14px",
												color: "black",
												textTransform: "none",
												fontFamily: "MAN EUROPE_Bold",
												fontWeight: "500",
											}}
										>
											<div
												style={{
													display: "flex",
													alignItems: "center",
													justifyContent: "center",
													gap: "6px",
												}}
											>
												SET alloc.
												<Edit2
													size={12}
													style={{ color: "#cc0000", opacity: 0.7 }}
												/>
												<Tooltip
													title="Every part has a designated team assigned to work on it. This team is called SET"
													arrow
												>
													<Info
														size={14}
														style={{
															color: "black",
															opacity: 0.7,
															transition: "opacity 0.2s",
														}}
														onMouseEnter={(e) => (e.target.style.opacity = "1")}
														onMouseLeave={(e) =>
															(e.target.style.opacity = "0.7")
														}
													/>
												</Tooltip>
											</div>
										</th>
									)}
									<th
										style={{
											padding: "12px",
											textAlign: "center",
											fontSize: "14px",
											color: "black",
											textTransform: "none",
											fontFamily: "MAN EUROPE_Bold",
											fontWeight: "500",
										}}
									>
										Dept
									</th>
									{columnVisibility.purchaseType && (
										<th
											style={{
												padding: "0",
												textAlign: "center",
												fontSize: "14px",
												color: "black",
												textTransform: "none",
												fontFamily: "MAN EUROPE_Bold",
												fontWeight: "500",
											}}
										>
											<div
												style={{
													padding: "12px",
													display: "flex",
													alignItems: "center",
													justifyContent: "center",
													gap: "6px",
												}}
											>
												Purchase Type
												<Tooltip
													title="The way in which the part will be procured. E.g., KT (Kaufteil) purchase type, HT (Hausteil) Inhouse part"
													arrow
												>
													<Info
														size={14}
														style={{
															color: "black",
															opacity: 0.7,
															transition: "opacity 0.2s",
														}}
														onMouseEnter={(e) => (e.target.style.opacity = "1")}
														onMouseLeave={(e) =>
															(e.target.style.opacity = "0.7")
														}
													/>
												</Tooltip>
											</div>
											<div
												style={{
													display: "flex",
													borderTop: "1px solid #e0e0e0",
													fontSize: "12px",
													fontWeight: "500",
												}}
											>
												<div
													style={{
														flex: 1,
														padding: "4px",
														borderRight: "1px solid #e0e0e0",
														backgroundColor: "#f5f5f5",
														borderLeft: "1px solid #e0e0e0",
														display: "flex",
														alignItems: "center",
														justifyContent: "center",
														gap: "4px",
													}}
												>
													BY SET
													<Edit2
														size={12}
														style={{ color: "#cc0000", opacity: 0.7 }}
													/>
													<Tooltip
														title="Purchase type selected by SET"
														arrow
														placement="top"
													>
														<Info
															size={12}
															style={{
																color: "black",
																opacity: 0.6,
																transition: "opacity 0.2s",
															}}
															onMouseEnter={(e) =>
																(e.target.style.opacity = "1")
															}
															onMouseLeave={(e) =>
																(e.target.style.opacity = "0.6")
															}
														/>
													</Tooltip>
												</div>
												<div
													style={{
														flex: 1,
														padding: "4px",
														backgroundColor: "#f5f5f5",
														borderRight: "1px solid #e0e0e0",
														display: "flex",
														alignItems: "center",
														justifyContent: "center",
														gap: "4px",
													}}
												>
													BY SYSTEM
													<Tooltip
														title="Purchase type selected by system"
														arrow
														placement="top"
													>
														<Info
															size={12}
															style={{
																color: "black",
																opacity: 0.6,
																transition: "opacity 0.2s",
															}}
															onMouseEnter={(e) =>
																(e.target.style.opacity = "1")
															}
															onMouseLeave={(e) =>
																(e.target.style.opacity = "0.6")
															}
														/>
													</Tooltip>
												</div>
											</div>{" "}
										</th>
									)}
									{(selectedViewType === "Prototype" ||
										selectedViewType === "Both") && (
										<th
											style={{
												padding: "12px",
												textAlign: "center",
												fontSize: "14px",
												color: "black",
												textTransform: "none",
												fontFamily: "MAN EUROPE_Bold",
												fontWeight: "500",
											}}
										>
											<div
												style={{
													display: "flex",
													alignItems: "center",
													justifyContent: "center",
													gap: "6px",
												}}
											>
												Series Annual Vol
												<Edit2
													size={12}
													style={{ color: "#cc0000", opacity: 0.7 }}
												/>
												<Tooltip title="Total no. of parts required" arrow>
													<Info
														size={14}
														style={{
															color: "black",
															opacity: 0.7,
															transition: "opacity 0.2s",
														}}
														onMouseEnter={(e) => (e.target.style.opacity = "1")}
														onMouseLeave={(e) =>
															(e.target.style.opacity = "0.7")
														}
													/>
												</Tooltip>
											</div>
										</th>
									)}
									{(selectedViewType === "VFF" ||
										selectedViewType === "Series" ||
										selectedViewType === "Both") && (
										<th
											style={{
												padding: "12px",
												textAlign: "center",
												fontSize: "14px",
												color: "black",
												textTransform: "none",
												fontFamily: "MAN EUROPE_Bold",
												fontWeight: "500",
											}}
										>
											<div
												style={{
													display: "flex",
													alignItems: "center",
													justifyContent: "center",
													gap: "6px",
												}}
											>
												Series Annual Vol
												{(selectedViewType === "VFF" ||
													selectedViewType === "Series") && (
													<Edit2
														size={12}
														style={{ color: "#cc0000", opacity: 0.7 }}
													/>
												)}
											</div>
										</th>
									)}
									{columnVisibility.supplier && (
										<th
											style={{
												padding: "12px",
												textAlign: "center",
												fontSize: "14px",
												color: "black",
												textTransform: "none",
												fontFamily: "MAN EUROPE_Bold",
												fontWeight: "500",
											}}
										>
											<div
												style={{
													display: "flex",
													alignItems: "center",
													justifyContent: "center",
													gap: "6px",
												}}
											>
												Supplier
												<Tooltip
													title="Supplier from whom the part will be ordered"
													arrow
												>
													<Info
														size={14}
														style={{
															color: "black",
															opacity: 0.7,
															transition: "opacity 0.2s",
														}}
														onMouseEnter={(e) => (e.target.style.opacity = "1")}
														onMouseLeave={(e) =>
															(e.target.style.opacity = "0.7")
														}
													/>
												</Tooltip>
											</div>
										</th>
									)}
									<th
										style={{
											padding: "12px",
											textAlign: "center",
											fontSize: "14px",
											color: "black",
											textTransform: "none",
											width: "230px",
											fontFamily: "MAN EUROPE_Bold",
											fontWeight: "500",
										}}
									>
										<div
											style={{
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
												gap: "6px",
											}}
										>
											<Edit2
												size={14}
												style={{ color: "#cc0000", opacity: 0.9 }}
											/>
											Status
											<Tooltip
												title="Edit follow-up date, add comments, and toggle the tracking status"
												arrow
											>
												<Info
													size={14}
													style={{
														color: "black",
														opacity: 0.7,
														transition: "opacity 0.2s",
													}}
													onMouseEnter={(e) => (e.target.style.opacity = "1")}
													onMouseLeave={(e) => (e.target.style.opacity = "0.7")}
												/>
											</Tooltip>
										</div>
									</th>
								</tr>
							</thead>
							<tbody>
								{((selectedViewType === "Both" && displayGroupedData.length === 0) ||
									(selectedViewType !== "Both" && displayData.length === 0)) &&
								initialDataLoaded &&
								!isLoading ? (
									<tr>
										<td
											colSpan={
												1 + // serial number
												1 + // checkbox
												(selectedViewType === "Both" ? 1 : 4) + // Details column for Both, or PTSNR + Description + Change No + Change No Description for single views
												(columnVisibility.rampUpGroup ? 1 : 0) +
												(columnVisibility.seTeams ? 1 : 0) +
												(columnVisibility.purchaseType ? 1 : 0) +
												(selectedViewType === "Prototype" ||
												selectedViewType === "Both"
													? 1
													: 0) + // total req (only for Prototype and Both)
												(selectedViewType === "Both" ||
												selectedViewType === "VFF" ||
												selectedViewType === "Series"
													? 1
													: 0) + // Series Annual Vol
												(columnVisibility.supplier ? 1 : 0) +
												1 // merged Status column (actions + date + flag)
											}
											style={{
												padding: "250px 0px 0px 500px",
												position: "relative",
												left: "120px",
												color: "#cc0000",
												fontSize: "18px",
												fontFamily: "MAN EUROPE_Regular",
											}}
										>
											{" "}
											{/* Check if any filters are applied by looking at currentFilters */}
											{(currentFilters.project &&
												currentFilters.project.length > 0) ||
											(currentFilters.programm &&
												currentFilters.programm.length > 0) ||
											(currentFilters.purchaseType &&
												currentFilters.purchaseType.length > 0) ||
											(currentFilters.seTeams &&
												currentFilters.seTeams.length > 0) ||
											(currentFilters.rampUpGroup &&
												currentFilters.rampUpGroup.length > 0) ||
											(currentFilters.supplier &&
												currentFilters.supplier.length > 0) ||
											(currentFilters.flag && currentFilters.flag.length > 0) ||
											(currentFilters.aenderungsnummer &&
												currentFilters.aenderungsnummer.length > 0) ||
											(currentFilters.changeDescription &&
												currentFilters.changeDescription.length > 0) ||
											(currentFilters.fet && currentFilters.fet.length > 0) ||
											(currentFilters.dept && currentFilters.dept.length > 0) ||
											(currentFilters.partNoPrefix &&
												currentFilters.partNoPrefix.length > 0) ||
											(currentFilters.search &&
												currentFilters.search.trim() !== "")
												? "No data found matching the selected filters."
												: "No data available."}
										</td>
									</tr>
								) : selectedViewType === "Both" ? (
									// Use grouped data for Both view — groups with any flagging=Y row first
									[...displayGroupedData]
										.sort((a, b) => {
											const aFlagged =
												a.prototype?.flagging === "Y" || a.vff?.flagging === "Y"
													? 1
													: 0;
											const bFlagged =
												b.prototype?.flagging === "Y" || b.vff?.flagging === "Y"
													? 1
													: 0;
											return bFlagged - aFlagged;
										})
										.map((groupedItem, index) => (
											<React.Fragment
												key={`grouped-${groupedItem.snr}-${groupedItem.project}-${index}`}
											>
												{/* Prototype row (upper half) */}{" "}
												<tr
													style={{
														backgroundColor:
															index % 2 === 0 ? "#f9f9f9" : "white",
														borderBottom: "1px solid #e0e0e0",
													}}
												>
													<td
														style={{
															padding: "6px",
															textAlign: "center",
															verticalAlign: "middle",
															borderRight: "1px solid #e0e0e0",
															fontSize: "12px",
															fontWeight: "bold",
															color: "#666",
														}}
														rowSpan={2}
													>
														{index + 1}
													</td>
													<td
														style={{
															padding: "6px",
															textAlign: "center",
															verticalAlign: "middle",
															borderRight: "1px solid #e0e0e0",
														}}
														rowSpan={2}
													>
														<div
															style={{
																display: "flex",
																flexDirection: "column",
																alignItems: "center",
																gap: "6px",
															}}
														>
															<input
																type="checkbox"
																checked={selectedItems.some(
																	(selected) =>
																		getItemKey(selected) ===
																		getItemKey(
																			groupedItem.prototype || {
																				snr: groupedItem.snr,
																				project: groupedItem.project,
																			},
																		),
																)}
																onChange={() =>
																	handleSelectItem(
																		groupedItem.prototype || {
																			snr: groupedItem.snr,
																			project: groupedItem.project,
																		},
																	)
																}
																style={{
																	cursor: "pointer",
																	width: "16px",
																	height: "16px",
																}}
															/>{" "}
															<div
																style={{
																	display: "flex",
																	flexDirection: "column",
																	alignItems: "center",
																	gap: "2px",
																}}
															>
																<FlagIcon
																	item={{
																		...(groupedItem.prototype || {
																			snr: groupedItem.snr,
																			flagging: "N",
																		}),
																		source_type: "Prototype",
																		category: "prototype",
																	}}
																/>
																<FlagIcon
																	item={{
																		...(groupedItem.vff || {
																			snr: groupedItem.snr,
																			flagging: "N",
																		}),
																		source_type: "VFF",
																		category: "vff",
																	}}
																/>
															</div>
														</div>
													</td>
													<td
														style={{
															padding: "6px",
															verticalAlign: "middle",
															width: "25%",
															minWidth: "200px",
														}}
													>
														<div
															style={{
																fontSize: "12px",
																lineHeight: "1.2",
																minWidth: "200px",
																wordBreak: "break-word",
															}}
														>
															<div
																style={{
																	backgroundColor: "#1e2832",
																	color: "white",
																	padding: "1px 4px",
																	borderRadius: "2px",
																	fontSize: "10px",
																	display: "inline-block",
																	marginBottom: "4px",
																}}
															>
																PROTOTYPE
															</div>
															{groupedItem.prototype ? (
																<>
																	<div
																		style={{
																			fontSize: "12px",
																			lineHeight: "1.2",
																			fontFamily: "MAN EUROPE_Regular",
																			fontWeight: "bold",
																		}}
																	>
																		<strong>Project no:</strong>{" "}
																		{groupedItem.prototype.project}{" "}
																	</div>
																	<div
																		style={{
																			fontSize: "12px",
																			lineHeight: "1.2",
																			fontFamily: "MAN EUROPE_Regular",
																			fontWeight: "bold",
																		}}
																	>
																		<strong>PTSNR:</strong>{" "}
																		{groupedItem.prototype.ptsnr}
																	</div>
																	<div>
																		<strong>SNR:</strong>{" "}
																		{groupedItem.prototype.snr}
																	</div>
																	<div>
																		<strong>Description:</strong>{" "}
																		{groupedItem.prototype.description}
																	</div>
																	<div>
																		<strong>Change No:</strong>{" "}
																		{groupedItem.prototype.aenderungsnummer ||
																			"-"}
																	</div>
																</>
															) : (
																<div
																	style={{
																		color: "#999",
																		fontStyle: "italic",
																		padding: "10px 0",
																	}}
																>
																	No prototype data available
																</div>
															)}
														</div>
													</td>
													{columnVisibility.rampUpGroup && (
														<td
															style={{
																padding: "6px",
																textAlign: "center",
																verticalAlign: "middle",
															}}
														>
															<div style={{ fontSize: "12px" }}>
																{groupedItem.prototype?.ramp_up_group || ""}
															</div>
														</td>
													)}
													{columnVisibility.seTeams && (
														<td
															style={{
																padding: "6px",
																verticalAlign: "middle",
															}}
														>
															{groupedItem.prototype &&
															isItemBeingEdited(
																groupedItem.prototype,
																"prototype",
															) ? (
																<SeTeamEditCell
																	value={editData.se_teams}
																	options={setAllocations}
																	onChange={(val) =>
																		handleInputChange("se_teams", val)
																	}
																/>
															) : (
																<div
																	style={{
																		color: "black",
																		padding: "2px 4px",
																		borderRadius: "2px",
																		fontSize: "12px",
																		display: "inline-block",
																		cursor: "pointer",
																		minHeight: "18px",
																		minWidth: "40px",
																		border: "1px dashed #999",
																	}}
																>
																	{groupedItem.prototype?.se_teams || ""}
																</div>
															)}
														</td>
													)}
													{/* Dept cell - prototype row */}
													<td
														style={{
															padding: "6px",
															textAlign: "center",
															verticalAlign: "middle",
															fontSize: "12px",
														}}
													>
														{groupedItem.prototype?.dept || ""}
													</td>
													{columnVisibility.purchaseType && (
														<td style={{ padding: "0", verticalAlign: "top" }}>
															<div style={{ display: "flex", height: "100%" }}>
																<div
																	style={{
																		flex: 1,
																		padding: "6px 2px",
																		borderRight: "1px solid #e0e0e0",
																		display: "flex",
																		alignItems: "center",
																		justifyContent: "center",
																		backgroundColor:
																			index % 2 === 0 ? "#f9f9f9" : "white",
																		minHeight: "25px",
																	}}
																>
																	{groupedItem.prototype &&
																	isItemBeingEdited(
																		groupedItem.prototype,
																		"prototype",
																	) ? (
																		<select
																			name="purchase_type"
																			value={editData.purchase_type || ""}
																			onChange={(e) =>
																				handleInputChange(
																					"purchase_type",
																					e.target.value,
																				)
																			}
																			style={{
																				width: "100%",
																				padding: "3px",
																				border: "1px solid #ccc",
																				borderRadius: "4px",
																				fontSize: "11px",
																				backgroundColor: "white",
																			}}
																		>
																			<option value="">Select</option>
																			{purchaseTypes.map((type) => (
																				<option key={type} value={type}>
																					{type}
																				</option>
																			))}
																		</select>
																	) : (
																		<span
																			style={{
																				...(groupedItem.prototype
																					? getPurchaseTypeStyleBySet(
																							groupedItem.prototype,
																						)
																					: {}),
																				padding: "2px 4px",
																				borderRadius: "2px",
																				cursor: "pointer",
																				minHeight: "18px",
																				display: "inline-block",
																				minWidth: "40px",
																				border: "1px dashed #999",
																			}}
																		>
																			{groupedItem.prototype?.purchase_type ||
																				""}
																		</span>
																	)}
																</div>
																<div
																	style={{
																		flex: 1,
																		padding: "6px 2px",
																		display: "flex",
																		alignItems: "center",
																		justifyContent: "center",
																		backgroundColor:
																			index % 2 === 0 ? "#f9f9f9" : "white",
																		minHeight: "25px",
																	}}
																>
																	<span
																		style={
																			groupedItem.prototype
																				? getPurchaseTypeStyleBySystem(
																						groupedItem.prototype,
																					)
																				: {}
																		}
																	>
																		{groupedItem.prototype
																			?.purchase_type_system || ""}
																	</span>
																</div>
															</div>
														</td>
													)}
													<td
														style={{ padding: "6px", verticalAlign: "middle" }}
													>
														{groupedItem.prototype &&
														isItemBeingEdited(
															groupedItem.prototype,
															"prototype",
														) ? (
															<input
																type="text"
																name="total_req"
																value={editData.total_req || ""}
																onChange={(e) =>
																	handleInputChange("total_req", e.target.value)
																}
																style={{
																	width: "100%",
																	padding: "3px",
																	border: "1px solid #ccc",
																	borderRadius: "4px",
																	fontSize: "12px",
																}}
															/>
														) : (
															<div
																style={{
																	color: "black",
																	padding: "2px 4px",
																	borderRadius: "2px",
																	fontSize: "12px",
																	display: "inline-block",
																	cursor: "pointer",
																	minHeight: "18px",
																	minWidth: "40px",
																	border: "1px dashed #999",
																}}
															>
																{groupedItem.prototype?.total_req || ""}
															</div>
														)}
													</td>
													<td
														style={{ padding: "6px", verticalAlign: "middle" }}
													>
														{groupedItem.prototype?.series_req || ""}
													</td>
													{columnVisibility.supplier && (
														<td
															style={{
																padding: "6px",
																verticalAlign: "middle",
															}}
														>
															<span
																style={{
																	color: "black",
																	padding: "2px 4px",
																	borderRadius: "2px",
																	fontSize: "12px",
																	display: "inline-block",
																	minHeight: "18px",
																	minWidth: "40px",
																}}
															>
																{groupedItem.prototype?.supplier || ""}
															</span>
														</td>
													)}
													{/* Merged Status cell for prototype row */}
													<td
														style={{
															padding: "6px 16px 6px 6px",
															verticalAlign: "middle",
															textAlign: "right",
															width: "230px",
															borderRight: "1px solid #e0e0e0",
															backgroundColor:
																index % 2 === 0 ? "#f9f9f9" : "white",
															position: "relative",
														}}
													>
														<div
															style={{
																display: "flex",
																flexDirection: "column",
																gap: "6px",
																alignItems: "flex-end",
															}}
														>
															{groupedItem.prototype && (
																<FollowupDateCell
																	item={{
																		...groupedItem.prototype,
																		source_type: "Prototype",
																		category: "prototype",
																	}}
																/>
															)}
															<div
																style={{
																	display: "flex",
																	gap: "2px",
																	justifyContent: "flex-end",
																	alignItems: "center",
																	flexWrap: "wrap",
																}}
															>
																{groupedItem.prototype &&
																isItemBeingEdited(
																	groupedItem.prototype,
																	"prototype",
																) ? (
																	<>
																		<button
																			onClick={handleSave}
																			disabled={saving}
																			style={{
																				background: "#cc0000",
																				color: "white",
																				border: "none",
																				borderRadius: "4px",
																				padding: "3px 6px",
																				cursor: "pointer",
																				display: "flex",
																				alignItems: "center",
																				gap: "2px",
																				fontSize: "10px",
																				whiteSpace: "nowrap",
																			}}
																		>
																			<Save size={12} />
																			{saving ? "Saving..." : ""}
																		</button>
																		<button
																			onClick={handleCancelEdit}
																			disabled={saving}
																			style={{
																				background: "#1e2832",
																				color: "white",
																				border: "none",
																				borderRadius: "4px",
																				padding: "3px 6px",
																				cursor: "pointer",
																				display: "flex",
																				alignItems: "center",
																				gap: "2px",
																				fontSize: "10px",
																				whiteSpace: "nowrap",
																			}}
																		>
																			<X size={12} />
																		</button>
																	</>
																) : (
																	<div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "2px" }}>
																		{groupedItem.prototype && canWrite && (
																			<button
																				onClick={() =>
																					handleEdit(
																						groupedItem.prototype,
																						"prototype",
																					)
																				}
																				className="edit-icon action-buttons"
																				style={{
																					background: "#cc0000",
																					color: "white",
																					border: "none",
																					borderRadius: "4px",
																					padding: "3px 6px",
																					cursor: "pointer",
																					display: "flex",
																					alignItems: "center",
																					gap: "2px",
																					fontSize: "10px",
																					whiteSpace: "nowrap",
																				}}
																			>
																				<Edit2 size={12} />
																				Edit
																			</button>
																		)}
																		{groupedItem.prototype && (
																			<button
																				onClick={() =>
																					handleOpenCommentModal(
																						groupedItem.prototype,
																						"prototype",
																					)
																				}
																				className="comment-icon comments-section action-buttons"
																				style={{
																					background: "#1e2832",
																					color: "white",
																					border: "none",
																					borderRadius: "4px",
																					padding: "3px 6px",
																					cursor: "pointer",
																					display: "flex",
																					alignItems: "center",
																					gap: "2px",
																					fontSize: "10px",
																					whiteSpace: "nowrap",
																				}}
																			>
																				<MessageSquare size={12} />
																			</button>
																		)}
																		<FlagToggle
																			item={{
																				...(groupedItem.prototype || {
																					snr: groupedItem.snr,
																					flag: "N",
																				}),
																				source_type: "Prototype",
																				category: "prototype",
																			}}
																		/>
																	</div>
																)}
															</div>
														</div>
													</td>
												</tr>
												{/* VFF row (lower half) */}
												<tr
													style={{
														backgroundColor:
															index % 2 === 0 ? "#f9f9f9" : "white",
														borderBottom: "2px solid black",
													}}
												>
													<td
														style={{
															padding: "6px",
															verticalAlign: "middle",
															width: "25%",
															minWidth: "300px",
														}}
													>
														<div
															style={{
																fontSize: "12px",
																lineHeight: "1.2",
																minWidth: "250px",
																wordBreak: "break-word",
															}}
														>
															<div
																style={{
																	backgroundColor: "#cc0000",
																	color: "white",
																	padding: "1px 4px",
																	borderRadius: "2px",
																	fontSize: "10px",
																	display: "inline-block",
																	marginBottom: "4px",
																}}
															>
																VFF
															</div>
															{groupedItem.vff ? (
																<>
																	<div
																		style={{
																			fontSize: "12px",
																			lineHeight: "1.2",
																			fontFamily: "MAN EUROPE_Regular",
																			fontWeight: "bold",
																		}}
																	>
																		<strong>Project no:</strong>{" "}
																		{groupedItem.vff.project}{" "}
																	</div>
																	<div
																		style={{
																			fontSize: "12px",
																			lineHeight: "1.2",
																			fontFamily: "MAN EUROPE_Regular",
																			fontWeight: "bold",
																		}}
																	>
																		<strong>VMSNR:</strong>{" "}
																		{groupedItem.vff.vmsnr ||
																			groupedItem.vff.ptsnr}
																	</div>
																	<div>
																		<strong>SNR:</strong> {groupedItem.vff.snr}
																	</div>
																	<div>
																		<strong>Description:</strong>{" "}
																		{groupedItem.vff.description}
																	</div>
																	<div>
																		<strong>Change No:</strong>{" "}
																		{groupedItem.vff.aenderungsnummer || "-"}
																	</div>
																</>
															) : (
																<div
																	style={{
																		color: "#999",
																		fontStyle: "italic",
																		padding: "10px 0",
																	}}
																>
																	No VFF data available
																</div>
															)}
														</div>
													</td>
													{columnVisibility.rampUpGroup && (
														<td
															style={{
																padding: "6px",
																textAlign: "center",
																verticalAlign: "middle",
															}}
														>
															<div style={{ fontSize: "12px" }}>
																{groupedItem.vff?.ramp_up_group || ""}
															</div>
														</td>
													)}
													{columnVisibility.seTeams && (
														<td
															style={{
																padding: "6px",
																verticalAlign: "middle",
															}}
														>
															{groupedItem.vff &&
															isItemBeingEdited(groupedItem.vff, "vff") ? (
																<SeTeamEditCell
																	value={editData.se_teams}
																	options={setAllocations}
																	onChange={(val) =>
																		handleInputChange("se_teams", val)
																	}
																/>
															) : (
																<div
																	style={{
																		color: "black",
																		padding: "2px 4px",
																		borderRadius: "2px",
																		fontSize: "12px",
																		display: "inline-block",
																		cursor: "pointer",
																		minHeight: "18px",
																		minWidth: "40px",
																		border: "1px dashed #999",
																	}}
																>
																	{" "}
																	{groupedItem.vff?.se_teams || ""}
																</div>
															)}
														</td>
													)}
													{/* Dept cell - VFF row */}
													<td
														style={{
															padding: "6px",
															textAlign: "center",
															verticalAlign: "middle",
															fontSize: "12px",
														}}
													>
														{groupedItem.vff?.dept || ""}
													</td>
													{columnVisibility.purchaseType && (
														<td style={{ padding: "0", verticalAlign: "top" }}>
															<div style={{ display: "flex", height: "100%" }}>
																<div
																	style={{
																		flex: 1,
																		padding: "6px 2px",
																		borderRight: "1px solid #e0e0e0",
																		display: "flex",
																		alignItems: "center",
																		justifyContent: "center",
																		backgroundColor:
																			index % 2 === 0 ? "#f9f9f9" : "white",
																		minHeight: "25px",
																	}}
																>
																	{groupedItem.vff &&
																	isItemBeingEdited(groupedItem.vff, "vff") ? (
																		<select
																			name="purchase_type"
																			value={editData.purchase_type || ""}
																			onChange={(e) =>
																				handleInputChange(
																					"purchase_type",
																					e.target.value,
																				)
																			}
																			style={{
																				width: "100%",
																				padding: "3px",
																				border: "1px solid #ccc",
																				borderRadius: "4px",
																				fontSize: "11px",
																				backgroundColor: "white",
																			}}
																		>
																			<option value="">Select</option>
																			{purchaseTypes.map((type) => (
																				<option key={type} value={type}>
																					{type}
																				</option>
																			))}
																		</select>
																	) : (
																		<span
																			style={{
																				...(groupedItem.vff
																					? getPurchaseTypeStyleBySet(
																							groupedItem.vff,
																						)
																					: {}),
																				padding: "2px 4px",
																				borderRadius: "2px",
																				cursor: "pointer",
																				minHeight: "18px",
																				display: "inline-block",
																				minWidth: "40px",
																				border: "1px dashed #999",
																			}}
																		>
																			{groupedItem.vff?.purchase_type || ""}
																		</span>
																	)}
																</div>
																<div
																	style={{
																		flex: 1,
																		padding: "6px 2px",
																		display: "flex",
																		alignItems: "center",
																		justifyContent: "center",
																		backgroundColor:
																			index % 2 === 0 ? "#f9f9f9" : "white",
																		minHeight: "25px",
																	}}
																>
																	<span
																		style={
																			groupedItem.vff
																				? getPurchaseTypeStyleBySystem(
																						groupedItem.vff,
																					)
																				: {}
																		}
																	>
																		{groupedItem.vff?.purchase_type_system ||
																			""}
																	</span>
																</div>
															</div>{" "}
														</td>
													)}
													<td
														style={{ padding: "6px", verticalAlign: "middle" }}
													>
														{groupedItem.vff &&
														isItemBeingEdited(groupedItem.vff, "vff") ? (
															<input
																type="text"
																name="total_req"
																value={editData.total_req || ""}
																onChange={(e) =>
																	handleInputChange("total_req", e.target.value)
																}
																style={{
																	width: "100%",
																	padding: "3px",
																	border: "1px solid #ccc",
																	borderRadius: "4px",
																	fontSize: "12px",
																}}
															/>
														) : (
															<div
																style={{
																	color: "black",
																	padding: "2px 4px",
																	borderRadius: "2px",
																	fontSize: "12px",
																	display: "inline-block",
																	cursor: "pointer",
																	minHeight: "18px",
																	minWidth: "40px",
																	border: "1px dashed #999",
																}}
															>
																{" "}
																{groupedItem.vff?.total_req || ""}
															</div>
														)}
													</td>
													<td
														style={{
															padding: "6px",
															verticalAlign: "middle",
															textAlign: "center",
														}}
													>
														{groupedItem.vff &&
														isItemBeingEdited(groupedItem.vff, "vff") ? (
															<input
																type="text"
																name="series_req"
																value={editData.series_req || ""}
																onChange={(e) =>
																	handleInputChange(
																		"series_req",
																		e.target.value,
																	)
																}
																style={{
																	width: "100%",
																	padding: "3px",
																	border: "1px solid #ccc",
																	borderRadius: "4px",
																	fontSize: "12px",
																}}
															/>
														) : (
															<div
																style={{
																	color: "black",
																	padding: "2px 4px",
																	borderRadius: "2px",
																	fontSize: "12px",
																	display: "inline-block",
																	cursor: "pointer",
																	minHeight: "18px",
																	minWidth: "40px",
																	border: "1px dashed #999",
																}}
															>
																{" "}
																{groupedItem.vff?.series_req || ""}
															</div>
														)}
													</td>
													{columnVisibility.supplier && (
														<td
															style={{
																padding: "6px",
																verticalAlign: "middle",
															}}
														>
															<span
																style={{
																	color: "black",
																	padding: "2px 4px",
																	borderRadius: "2px",
																	fontSize: "12px",
																	display: "inline-block",
																	minHeight: "18px",
																	minWidth: "40px",
																}}
															>
																{groupedItem.vff?.supplier || ""}
															</span>
														</td>
													)}
													{/* Merged Status cell for VFF row */}
													<td
														style={{
															padding: "6px 16px 6px 6px",
															verticalAlign: "middle",
															textAlign: "right",
															width: "230px",
															position: "relative",
														}}
													>
														<div
															style={{
																display: "flex",
																flexDirection: "column",
																gap: "6px",
																alignItems: "flex-end",
															}}
														>
															{groupedItem.vff && (
																<FollowupDateCell
																	item={{
																		...groupedItem.vff,
																		source_type: "VFF",
																		category: "vff",
																	}}
																/>
															)}
															<div
																style={{
																	display: "flex",
																	gap: "2px",
																	justifyContent: "flex-end",
																	alignItems: "center",
																	flexWrap: "wrap",
																}}
															>
																{groupedItem.vff &&
																isItemBeingEdited(groupedItem.vff, "vff") ? (
																	<>
																		<button
																			onClick={handleSave}
																			disabled={saving}
																			style={{
																				background: "#cc0000",
																				color: "white",
																				border: "none",
																				borderRadius: "4px",
																				padding: "3px 6px",
																				cursor: "pointer",
																				display: "flex",
																				alignItems: "center",
																				gap: "2px",
																				fontSize: "10px",
																				whiteSpace: "nowrap",
																			}}
																		>
																			<Save size={12} />
																			{saving ? "Saving..." : ""}
																		</button>
																		<button
																			onClick={handleCancelEdit}
																			disabled={saving}
																			style={{
																				background: "#1e2832",
																				color: "white",
																				border: "none",
																				borderRadius: "4px",
																				padding: "3px 6px",
																				cursor: "pointer",
																				display: "flex",
																				alignItems: "center",
																				gap: "2px",
																				fontSize: "10px",
																				whiteSpace: "nowrap",
																			}}
																		>
																			<X size={12} />
																		</button>
																	</>
																) : (
																	<div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "2px" }}>
																		{groupedItem.vff && canWrite && (
																			<button
																				onClick={() =>
																					handleEdit(groupedItem.vff, "vff")
																				}
																				className="edit-icon action-buttons"
																				style={{
																					background: "#cc0000",
																					color: "white",
																					border: "none",
																					borderRadius: "4px",
																					padding: "3px 6px",
																					cursor: "pointer",
																					display: "flex",
																					alignItems: "center",
																					gap: "2px",
																					fontSize: "10px",
																					whiteSpace: "nowrap",
																				}}
																			>
																				<Edit2 size={12} />
																				Edit
																			</button>
																		)}
																		{groupedItem.vff && (
																			<button
																				onClick={() =>
																					handleOpenCommentModal(
																						groupedItem.vff,
																						"vff",
																					)
																				}
																				className="comment-icon comments-section action-buttons"
																				style={{
																					background: "#1e2832",
																					color: "white",
																					border: "none",
																					borderRadius: "4px",
																					padding: "3px 6px",
																					cursor: "pointer",
																					display: "flex",
																					alignItems: "center",
																					gap: "2px",
																					fontSize: "10px",
																					whiteSpace: "nowrap",
																				}}
																			>
																				<MessageSquare size={12} />
																			</button>
																		)}
																		<FlagToggle
																			item={{
																				...(groupedItem.vff || {
																					snr: groupedItem.snr,
																					flag: "N",
																				}),
																				source_type: "VFF",
																				category: "vff",
																			}}
																		/>
																	</div>
																)}
															</div>
														</div>
													</td>
												</tr>
											</React.Fragment>
										))
								) : (
									// Single view (Prototype or VFF) — flagging=Y rows first
									[...displayData]
										.sort(
											(a, b) =>
												(b.flagging === "Y" ? 1 : 0) -
												(a.flagging === "Y" ? 1 : 0),
										)
										.map((item, index) => (
											<tr
												key={`item-${item.snr}-${index}`}
												style={{
													backgroundColor:
														index % 2 === 0 ? "#f9f9f9" : "white",
													borderBottom: "1px solid #e0e0e0",
												}}
											>
												<td
													style={{
														padding: "6px",
														textAlign: "center",
														verticalAlign: "middle",
														fontSize: "12px",
														fontWeight: "bold",
														color: "#666",
													}}
												>
													{index + 1}
												</td>
												<td
													style={{
														padding: "6px",
														textAlign: "center",
														verticalAlign: "middle",
													}}
												>
													<div
														style={{
															display: "flex",
															alignItems: "center",
															justifyContent: "center",
															gap: "4px",
														}}
													>
														<input
															type="checkbox"
															checked={selectedItems.some(
																(selected) =>
																	getItemKey(selected) === getItemKey(item),
															)}
															onChange={() => handleSelectItem(item)}
															style={{
																cursor: "pointer",
																width: "16px",
																height: "16px",
															}}
														/>
														<FlagIcon item={item} />
													</div>
												</td>
												<td
													style={{
														padding: "6px",
														verticalAlign: "middle",
														textAlign: "center",
													}}
												>
													<div
														style={{
															fontSize: "12px",
															fontFamily: "MAN EUROPE_Regular",
														}}
													>
														{selectedViewType === "VFF"
															? item.vmsnr || item.ptsnr
															: item.ptsnr}
													</div>
													<button
														data-tour-target="ptsachnr-more-info"
														onClick={(e) => {
															e.stopPropagation();
															if (sidePanelOpen && selectedSidePanelRow && selectedSidePanelRow.ptsnr === item.ptsnr && selectedSidePanelRow.projekt === item.projekt) {
																setSidePanelOpen(false);
																setSelectedSidePanelRow(null);
															} else {
																setSelectedSidePanelRow(item);
																setSidePanelOpen(true);
															}
														}}
														style={{
															background:
																sidePanelOpen &&
																selectedSidePanelRow &&
																selectedSidePanelRow.ptsnr === item.ptsnr &&
																selectedSidePanelRow.projekt === item.projekt
																	? "#cc0000"
																	: "none",
															border:
																sidePanelOpen &&
																selectedSidePanelRow &&
																selectedSidePanelRow.ptsnr === item.ptsnr &&
																selectedSidePanelRow.projekt === item.projekt
																	? "1px solid #cc0000"
																	: "1px solid #ccc",
															padding: "2px 6px",
															cursor: "pointer",
															fontSize: "12px",
															color:
																sidePanelOpen &&
																selectedSidePanelRow &&
																selectedSidePanelRow.ptsnr === item.ptsnr &&
																selectedSidePanelRow.projekt === item.projekt
																	? "white"
																	: "#666",
															display: "flex",
															alignItems: "center",
															gap: "3px",
															marginTop: "3px",
															fontFamily: "MAN EUROPE_Regular",
														}}
													>
														{sidePanelOpen &&
														selectedSidePanelRow &&
														selectedSidePanelRow.ptsnr === item.ptsnr &&
														selectedSidePanelRow.projekt === item.projekt
															? "Close info"
															: "More info"}
														<ChevronRight size={12} />
													</button>
												</td>
												<td
													style={{
														padding: "6px",
														verticalAlign: "middle",
														maxWidth: "200px",
														textAlign: "center",
													}}
												>
													<div
														style={{
															fontSize: "12px",
															fontFamily: "MAN EUROPE_Regular",
															overflow: "hidden",
															textOverflow: "ellipsis",
															whiteSpace: "nowrap",
															maxWidth: "100%",
														}}
													>
														{item.description ? (
															<Tooltip
																title={item.description}
																placement="top"
																arrow
															>
																<span
																	style={{
																		overflow: "hidden",
																		textOverflow: "ellipsis",
																		whiteSpace: "nowrap",
																		display: "block",
																	}}
																>
																	{item.description}
																</span>
															</Tooltip>
														) : (
															"-"
														)}
													</div>{" "}
												</td>
												<td
													style={{
														padding: "6px 12px",
														verticalAlign: "middle",
														textAlign: "center",
													}}
												>
													<div
														style={{
															fontSize: "12px",
															fontFamily: "MAN EUROPE_Regular",
														}}
													>
														{item.aenderungsnummer || "-"}
													</div>
												</td>
												<td
													style={{
														padding: "6px 12px",
														verticalAlign: "middle",
														maxWidth: "200px",
														textAlign: "center",
													}}
												>
													<div
														style={{
															fontSize: "12px",
															fontFamily: "MAN EUROPE_Regular",
															overflow: "hidden",
															textOverflow: "ellipsis",
															whiteSpace: "nowrap",
															maxWidth: "100%",
														}}
													>
														{item.change_no_description ? (
															<Tooltip
																title={item.change_no_description}
																placement="top"
																arrow
															>
																<span
																	style={{
																		overflow: "hidden",
																		textOverflow: "ellipsis",
																		whiteSpace: "nowrap",
																		display: "block",
																	}}
																>
																	{item.change_no_description}
																</span>
															</Tooltip>
														) : (
															"-"
														)}
													</div>
												</td>
												<td
													style={{
														padding: "6px",
														verticalAlign: "middle",
														textAlign: "center",
													}}
												>
													<div
														style={{
															fontSize: "12px",
															fontFamily: "MAN EUROPE_Regular",
														}}
													>
														{item.fet || "-"}
													</div>
												</td>
												{columnVisibility.rampUpGroup && (
													<td
														style={{
															padding: "6px",
															textAlign: "center",
															verticalAlign: "middle",
														}}
													>
														<div style={{ fontSize: "12px" }}>
															{item.ramp_up_group || "-"}
														</div>
													</td>
												)}
												{columnVisibility.seTeams && (
													<td
														style={{ padding: "6px", verticalAlign: "middle" }}
													>
														{isItemBeingEdited(item) ? (
															<SeTeamEditCell
																value={editData.se_teams}
																options={setAllocations}
																onChange={(val) =>
																	handleInputChange("se_teams", val)
																}
															/>
														) : (
															<div
																style={{
																	color: "black",
																	padding: "2px 4px",
																	borderRadius: "2px",
																	fontSize: "12px",
																	display: "inline-block",
																	cursor: "pointer",
																	minHeight: "18px",
																	minWidth: "40px",
																	border: "1px dashed #999",
																}}
															>
																{" "}
																{item.se_teams || "-"}
															</div>
														)}
													</td>
												)}
												{/* Dept cell - single view */}
												<td
													style={{
														padding: "6px",
														textAlign: "center",
														verticalAlign: "middle",
														fontSize: "12px",
													}}
												>
													{item.dept || "-"}
												</td>
												{columnVisibility.purchaseType && (
													<td style={{ padding: "0", verticalAlign: "top" }}>
														<div style={{ display: "flex", height: "100%" }}>
															<div
																style={{
																	flex: 1,
																	padding: "6px 4px",
																	borderRight: "1px solid #e0e0e0",
																	display: "flex",
																	alignItems: "center",
																	justifyContent: "center",
																	backgroundColor:
																		index % 2 === 0 ? "#f9f9f9" : "white",
																	minHeight: "25px",
																}}
															>
																{isItemBeingEdited(item) ? (
																	<select
																		name="purchase_type"
																		value={editData.purchase_type || ""}
																		onChange={(e) =>
																			handleInputChange(
																				"purchase_type",
																				e.target.value,
																			)
																		}
																		style={{
																			width: "100%",
																			padding: "4px",
																			border: "1px solid #ccc",
																			borderRadius: "4px",
																			fontSize: "12px",
																			backgroundColor: "white",
																		}}
																	>
																		<option value="">Select</option>
																		{purchaseTypes.map((type) => (
																			<option key={type} value={type}>
																				{type}
																			</option>
																		))}
																	</select>
																) : (
																	<span
																		style={{
																			...getPurchaseTypeStyleBySet(item),
																			padding: "2px 4px",
																			borderRadius: "2px",
																			cursor: "pointer",
																			minHeight: "18px",
																			display: "inline-block",
																			minWidth: "40px",
																			border: "1px dashed #999",
																		}}
																	>
																		{item.purchase_type || "-"}
																	</span>
																)}
															</div>
															<div
																style={{
																	flex: 1,
																	padding: "6px 4px",
																	display: "flex",
																	alignItems: "center",
																	justifyContent: "center",
																	backgroundColor:
																		index % 2 === 0 ? "#f9f9f9" : "white",
																	minHeight: "25px",
																}}
															>
																<span
																	style={getPurchaseTypeStyleBySystem(item)}
																>
																	{item.purchase_type_system || "-"}
																</span>
															</div>
														</div>{" "}
													</td>
												)}
												{(selectedViewType === "Prototype" ||
													selectedViewType === "Both") && (
													<td
														style={{
															padding: "6px",
															verticalAlign: "middle",
															textAlign: "center",
														}}
													>
														{isItemBeingEdited(item) ? (
															<input
																type="number"
																min="0"
																step="1"
																value={editData.total_req}
																onChange={(e) => {
																	// Only update if the input is a valid number or empty string
																	if (
																		e.target.value === "" ||
																		/^\d+$/.test(e.target.value)
																	) {
																		handleInputChange(
																			"total_req",
																			e.target.value,
																		);
																	}
																}}
																onKeyPress={(e) => {
																	// Prevent entering non-numeric characters
																	if (!/[0-9]/.test(e.key)) {
																		e.preventDefault();
																	}
																}}
																style={{
																	width: "100%",
																	padding: "3px",
																	boxSizing: "border-box",
																	border: "1px solid #ccc",
																	borderRadius: "4px",
																	textAlign: "center",
																	fontSize: "12px",
																}}
															/>
														) : (
															<div
																style={{
																	textAlign: "center",
																	padding: "4px 8px",
																	borderRadius: "2px",
																	cursor: "pointer",
																	minHeight: "18px",
																	display: "inline-block",
																	minWidth: "40px",
																	border: "1px dashed #999",
																}}
															>
																{item.total_req || "-"}
															</div>
														)}
													</td>
												)}
												{(selectedViewType === "VFF" ||
													selectedViewType === "Series" ||
													selectedViewType === "Both") && (
													<td
														style={{
															padding: "6px",
															verticalAlign: "middle",
															textAlign: "center",
														}}
													>
														{selectedViewType !== "Both" &&
														isItemBeingEdited(item) ? (
															<input
																type="text"
																name="series_req"
																value={editData.series_req || ""}
																onChange={(e) =>
																	handleInputChange(
																		"series_req",
																		e.target.value,
																	)
																}
																style={{
																	width: "100%",
																	padding: "3px",
																	boxSizing: "border-box",
																	border: "1px solid #ccc",
																	borderRadius: "4px",
																	textAlign: "center",
																	fontSize: "12px",
																}}
															/>
														) : (
															<div
																style={{
																	textAlign: "center",
																	padding: "4px 8px",
																	borderRadius: "2px",
																	cursor: "pointer",
																	minHeight: "18px",
																	display: "inline-block",
																	minWidth: "40px",
																	border:
																		selectedViewType !== "Both"
																			? "1px dashed #999"
																			: "none",
																}}
															>
																{" "}
																{item.series_req || "-"}
															</div>
														)}
													</td>
												)}
												{columnVisibility.supplier && (
													<td
														style={{
															padding: "6px",
															verticalAlign: "middle",
															textAlign: "center",
														}}
													>
														<span
															style={{
																color: "black",
																padding: "2px 4px",
																borderRadius: "2px",
																fontSize: "12px",
																display: "inline-block",
																minHeight: "18px",
																minWidth: "30px",
															}}
														>
															{item.supplier || "-"}
														</span>
													</td>
												)}
												<td
													style={{
														padding: "6px 16px 6px 6px",
														verticalAlign: "middle",
														textAlign: "right",
														width: "230px",
														backgroundColor:
															index % 2 === 0 ? "#f9f9f9" : "white",
														position: "relative",
													}}
												>
													<div
														style={{
															display: "flex",
															flexDirection: "column",
															gap: "6px",
															alignItems: "flex-end",
														}}
													>
														{/* Date row */}
														<FollowupDateCell item={item} />
														{/* Actions + Status toggle row */}
														<div
															style={{
																display: "flex",
																gap: "2px",
																justifyContent: "flex-end",
																alignItems: "center",
																width: "100%",
															}}
														>
															{isItemBeingEdited(item) ? (
																<>
																	<button
																		onClick={handleSave}
																		disabled={saving}
																		style={{
																			background: "#cc0000",
																			color: "white",
																			border: "none",
																			borderRadius: "4px",
																			padding: "4px 8px",
																			cursor: "pointer",
																			display: "flex",
																			alignItems: "center",
																			gap: "4px",
																			fontSize: "12px",
																			whiteSpace: "nowrap",
																		}}
																	>
																		<Save size={14} />
																	</button>
																	<button
																		onClick={handleCancelEdit}
																		disabled={saving}
																		style={{
																			background: "#1e2832",
																			color: "white",
																			border: "none",
																			borderRadius: "4px",
																			padding: "4px 8px",
																			cursor: "pointer",
																			display: "flex",
																			alignItems: "center",
																			gap: "4px",
																			fontSize: "12px",
																			whiteSpace: "nowrap",
																		}}
																	>
																		<X size={14} />
																	</button>
																</>
															) : (
																<div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "4px" }}>
																	{canWrite && (
																		<button
																			onClick={() => handleEdit(item)}
																			className="edit-icon action-buttons"
																			style={{
																				background: "#cc0000",
																				color: "white",
																				border: "none",
																				borderRadius: "4px",
																				padding: "4px 8px",
																				cursor: "pointer",
																				display: "flex",
																				alignItems: "center",
																				gap: "4px",
																				fontSize: "12px",
																				whiteSpace: "nowrap",
																			}}
																		>
																			<Edit2 size={14} />
																		</button>
																	)}{" "}
																	<button
																		onClick={() =>
																			handleOpenCommentModal(
																				item,
																				item.category ||
																					selectedViewType.toLowerCase(),
																			)
																		}
																		className="comment-icon action-buttons"
																		style={{
																			background: "#1e2832",
																			color: "white",
																			border: "none",
																			borderRadius: "4px",
																			padding: "4px 8px",
																			cursor: "pointer",
																			display: "flex",
																			alignItems: "center",
																			gap: "4px",
																			fontSize: "12px",
																			whiteSpace: "nowrap",
																		}}
																	>
																		<MessageSquare size={14} />
																	</button>
																	<FlagToggle item={item} />
																</div>
															)}
														</div>
													</div>
												</td>
											</tr>
										))
								)}
							</tbody>
						</table>
					</div>
					{/* Loading More Indicator */}
					{isLoadingMore && (
						<div
							style={{
								textAlign: "center",
								padding: "20px",
								borderTop: "1px solid #eee",
								backgroundColor: "#f8f9fa",
							}}
						>
							<CircularProgress size={20} sx={{ color: "#cc0000", mr: 1 }} />
							<Typography
								variant="body2"
								style={{
									display: "inline",
									marginLeft: "8px",
									color: "#666",
									fontSize: "12px",
								}}
							>
								Loading more records...
							</Typography>
						</div>
					)}{" "}
					{/* Load More button (fallback for manual loading) - Hidden by default, only shows if scroll fails */}
					{false &&
						!isLoading &&
						!isLoadingMore &&
						hasMoreData &&
						(selectedViewType === "Both" ? groupedData.length : data.length) >
							0 && (
							<div
								style={{
									textAlign: "center",
									padding: "20px",
									borderTop: "1px solid #eee",
									backgroundColor: "#f8f9fa",
								}}
							>
								<button
									onClick={loadMoreData}
									style={{
										padding: "8px 16px",
										backgroundColor: "#cc0000",
										color: "white",
										border: "none",
										borderRadius: "4px",
										cursor: "pointer",
										fontSize: "14px",
										fontFamily: "MAN EUROPE_Regular",
										display: "flex",
										alignItems: "center",
										gap: "8px",
										margin: "0 auto",
									}}
									onMouseOver={(e) =>
										(e.target.style.backgroundColor = "#b30000")
									}
									onMouseOut={(e) =>
										(e.target.style.backgroundColor = "#cc0000")
									}
								>
									Load More Records
								</button>
								<Typography
									variant="body2"
									style={{ marginTop: "8px", color: "#666", fontSize: "12px" }}
								>
									Or scroll down to auto-load
								</Typography>
							</div>
						)}
					{/* No more data indicator */}
					{!isLoading &&
						!isLoadingMore &&
						!hasMoreData &&
						(selectedViewType === "Both" ? groupedData.length : data.length) >
							0 && (
							<div
								style={{
									textAlign: "center",
									padding: "15px",
									borderTop: "1px solid #eee",
									backgroundColor: "#f8f9fa",
									color: "#666",
									fontSize: "14px",
									fontFamily: "MAN Europe_Bold",
								}}
							>
								All records loaded -{" "}
								{selectedViewType === "Both" ? groupedData.length : data.length}{" "}
								total records
							</div>
						)}{" "}
					<div
						style={{
							backgroundColor: "#f5f5f5",
							padding: "6px 12px",
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
							borderTop: "1px solid #ddd",
							position: "sticky",
							bottom: 0,
							width: "100%",
							boxShadow: "0 -2px 4px rgba(0,0,0,0.05)",
							minHeight: "32px",
						}}
					>
						{/* Left: spacer */}
						<div style={{ flex: 1 }} />
						{/* Center: record count */}
						<div
							style={{
								display: "flex",
								flexDirection: "column",
								alignItems: "center",
								flex: 1,
							}}
						>
							<span
								style={{
									fontSize: "12px",
									color: "#666",
									fontFamily: "MAN Europe_Bold",
								}}
							>
								Showing{" "}
								{selectedViewType === "Both" ? groupedData.length : data.length}{" "}
								of {totalRecords} records
							</span>
							{!hasMoreData &&
								(selectedViewType === "Both"
									? groupedData.length
									: data.length) > 0 && (
									<span
										style={{
											fontSize: "11px",
											color: "#059669",
											marginTop: "2px",
											fontFamily: "MAN Europe_Bold",
										}}
									>
										✅ All records loaded
									</span>
								)}
						</div>{" "}
						{/* Right: last DB update */}
						<div
							style={{
								display: "flex",
								flexDirection: "column",
								alignItems: "flex-end",
								flex: 1,
								paddingRight: "20px",
							}}
						>
							<span
								style={{
									fontSize: "11px",
									color: "#666",
									fontFamily: "MAN EUROPE_Regular",
								}}
							>
								Last DB Update (IST)
							</span>
							<span
								style={{
									fontSize: "12px",
									color: "#333",
									fontFamily: "MAN EUROPE_Bold",
									marginTop: "2px",
								}}
							>
								{lastDbUpdate
									? (() => {
											try {
												const date = new Date(lastDbUpdate);
												date.setHours(date.getHours() + 5);
												date.setMinutes(date.getMinutes() + 30);
												const year = date.getFullYear();
												const month = String(date.getMonth() + 1).padStart(
													2,
													"0",
												);
												const day = String(date.getDate()).padStart(2, "0");
												const hours = String(date.getHours()).padStart(2, "0");
												const minutes = String(date.getMinutes()).padStart(
													2,
													"0",
												);
												const seconds = String(date.getSeconds()).padStart(
													2,
													"0",
												);
												return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
											} catch (e) {
												return lastDbUpdate;
											}
										})()
									: "Loading..."}
							</span>
						</div>
					</div>
					{/* Old pagination footer removed - replaced with infinite scroll indicator above */}
					{/* Keeping original footer hidden for reference but disabled
        <div style={{ 
          backgroundColor: '#f5f5f5', 
          padding: '8px 20px', 
          display: 'none', // Hidden to use new infinite scroll footer
          alignItems: 'center', 
          justifyContent: 'center',
          borderTop: '1px solid #ddd',
          position: 'sticky',
          bottom: 0,
          width: '100%',
          boxShadow: '0 -2px 4px rgba(0,0,0,0.05)',
          minHeight: '50px'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '20px',
            backgroundColor: 'white',
            padding: '2px 20px',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
            border: '1px solid #ddd'
          }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '10px',
              flexWrap: 'wrap'
            }}>
              <button 
                onClick={prevPage}
                disabled={currentPage === 1}
                style={{ 
                  padding: '2px 2px', 
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  backgroundColor: currentPage === 1 ? '#f5f5f5' : 'white',
                  cursor: currentPage === 1 ? 'default' : 'pointer',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <ChevronLeft style={{ width: '18px', height: '18px', color: currentPage === 1 ? '#999' : '#666' }} />
              </button>

              <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center',
                minWidth: '200px'
              }}>                
              <span style={{ fontSize: '14px', color: '#666', fontWeight: 'bold' }}>
                  Page {currentPage} of {totalPages}
                </span>
                <span style={{ fontSize: '12px', color: '#888', marginTop: '4px' }}>
                  Records {totalRecords > 0 ? indexOfFirstItem : 0} - {indexOfLastItem} of {totalRecords}
                </span>
              </div>              
              <button 
                onClick={nextPage}
                disabled={currentPage === totalPages}
                style={{ 
                  padding: '2px 2px', 
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  backgroundColor: currentPage === totalPages ? '#f5f5f5' : 'white',
                  cursor: currentPage === totalPages ? 'default' : 'pointer',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <ChevronRight style={{ width: '18px', height: '18px', color: currentPage === totalPages ? '#999' : '#666' }} />
              </button>
            </div>

            <select
              value={itemsPerPage}
              onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
              style={{
                padding: '4px 6px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                backgroundColor: 'white',
                cursor: 'pointer',
                color: '#666',
                fontSize: '14px'
              }}
            >
              <option value={5}>5 per page</option>
              <option value={10}>10 per page</option>
              <option value={25}>25 per page</option>
              <option value={50}>50 per page</option>
              <option value={100}>100 per page</option>
            </select>
          </div>
        </div>
        */}
					{/* Loading Overlay for Save Operation */}
					{saving && (
						<div
							style={{
								position: "absolute",
								top: 0,
								left: 0,
								right: 0,
								bottom: 0,
								backgroundColor: "rgba(255, 255, 255, 0.9)",
								display: "flex",
								flexDirection: "column",
								justifyContent: "center",
								alignItems: "center",
								zIndex: 1000,
								borderRadius: "8px",
							}}
						>
							<div
								style={{
									width: "40px",
									height: "40px",
									border: "4px solid #f3f3f3",
									borderTop: "4px solid #cc0000",
									borderRadius: "50%",
									animation: "spin 1s linear infinite",
									marginBottom: "20px",
								}}
							></div>
							<div
								style={{
									fontSize: "16px",
									fontWeight: "500",
									color: "#333",
									marginBottom: "8px",
								}}
							>
								Saving changes...
							</div>
							<div
								style={{
									fontSize: "14px",
									color: "#666",
								}}
							>
								Please wait while we update the data
							</div>
						</div>
					)}
					{/* PTSachNr More Info Side Panel */}
					<PTSachNrSidePanel
						isOpen={sidePanelOpen}
						onClose={() => {
							setSidePanelOpen(false);
							setSelectedSidePanelRow(null);
						}}
						selectedRow={selectedSidePanelRow}
					/>
				</div>
			</>
		);
	},
);

export default PTSachNrDataTable;
